import { BaseAction } from "../../../../core/actions/BaseAction"
import { ExecutionStatus } from "../../../../core/enum/ExecutionStatus"
import { STORAGE_KEYS } from "../../../../Lib/Constants/storageKeys"
import { isBookingMultiBooking, preferTrainTrips } from "../../../../Lib/Utils/booking"
import { isLiveChangeDateCheckout } from "../../../../Lib/Utils/changeDate"
import { isNolAncillary } from "../../../../Lib/Utils/nolCard"
import { hasPendingExchange } from "../../../../Components/OnwardTravel/hasPendingExchange"
import { calculatePrice, filterTripsAndOffersWithPrebookStatus, getTrainClassTypeBaseOnProduct, getUpperPassengerLabelByAge, isActionRunSuccessfully, isInbound, isOutbound, isEmpty } from "../../../../Lib/Utils/validationUtil"
import { recoverBookingTripDirections } from "../../../../Lib/Utils/tripDirectionRecovery"
import { getRecoilStateValue, updateRecoilState } from "../../../../Orchestrator/Recoils/Events/Recoil"
import { AdmissionStatusEnum, AncillaryStatusEnum, ProductTypeEnum, PtModeEnum, SHUTTLE_PRODUCT_CODE, TrainTypeEnum, TripTypeEnum } from "../../../../Orchestrator/Types/Enums/globalDefaultEnum"
import { IBookedOffer, IBooking, IBookingTypeTrip, ILeg, ISelectedTrainLegs } from "../../../../Orchestrator/Types/Index"
import { IGetLegsFromTripActionParams } from "../../../../Orchestrator/Types/Interface/ActionParams/TrainActions"
import { IPaymentItemDetails } from "../../../../Orchestrator/Types/Interface/IPayment"
import { getLegsSummaryFromTripAction } from "../../../UIActions/TrainOffer/GetLegsFromTripAction"
import { GetAllOfferWithoutExchangeAction } from "../../CommonActions/GetAllOfferWithoutExchangeAction"
import { GetAllTripWithoutExchangeAction } from "../../CommonActions/GetAllTripWithoutExchangeAction"
import { updatePaymentItemsAction } from "../../CommonActions/UpdatePaymentItemsAction"
import { calculatePassengerListByPriceAction } from "../../Search/CalculatePassengerListByPriceAction"

/**
 * Admission statuses meaning the travel right is GONE — the passenger is no
 * longer being carried on that admission.
 *
 * NOT the complement of "owned": PREBOOKED and ON_HOLD are unpaid but the rider
 * is still on the offer and the checkout must count and price them. Only these
 * three remove a rider.
 *
 * Do NOT merge with `HISTORICAL_ADMISSION_STATUSES` in bookingEligibility.ts:
 * that set deliberately EXCLUDES EXCHANGED, because there an exchanged admission
 * still consumes the booking's one exchange slot. Different question, same shape.
 */
const TERMINAL_ADMISSION_STATUSES = new Set<string>([
    AdmissionStatusEnum.REFUNDED,
    AdmissionStatusEnum.RELEASED,
    AdmissionStatusEnum.EXCHANGED,
])

/**
 * The subset of terminal statuses the customer got their money back for.
 *
 * These still earn a visible line — struck through and badged "Refunded", the
 * same treatment a refunded ancillary gets — because the customer paid for that
 * shuttle and the summary is the record of it. EXCHANGED is deliberately absent:
 * an exchanged admission was superseded by a replacement offer that carries its
 * own row, so showing it as refunded would both misdescribe it and double-count
 * the journey.
 */
const REFUNDED_ADMISSION_STATUSES = new Set<string>([
    AdmissionStatusEnum.REFUNDED,
    AdmissionStatusEnum.RELEASED,
])

/**
 * Sum the prices of every EXCHANGE_ONGOING ancillary across all booked offers
 * on a booking. Walks raw `bookedOffers` (NOT the filtered slice) so a
 * carry-over on an EXCHANGE_ONGOING-admission original offer is captured
 * even if a downstream filter would have dropped that offer.
 *
 * Caller responsibility: invoke ONLY in change-date checkout. The math
 * here is "what value did the user already pay for that should not be in
 * the new Total" — semantically meaningless outside that flow.
 */
export function sumExchangeOngoingAncillaries(booking: IBooking | undefined | null): number {
    let sum = 0;
    // Flatten once: post-exchange the NOL product an ancillary references can
    // live on a sibling offer, not just the offer that owns it.
    const allProducts = ((booking?.bookedOffers || []) as IBookedOffer[]).flatMap((o) => (o as any)?.products || []);
    for (const offer of (booking?.bookedOffers || []) as IBookedOffer[]) {
        for (const anc of ((offer as any)?.ancillaries || []) as any[]) {
            // NOL day-passes ride the booking as ancillaries but belong to the
            // End-to-end Journey flow, not "Extras" — never count them as
            // already-paid credit in change-date. See [[isNolAncillary]].
            if (isNolAncillary(allProducts, anc)) continue;
            if (anc?.status === AncillaryStatusEnum.EXCHANGE_ONGOING) {
                sum += calculatePrice(anc?.price);
            }
        }
    }
    return sum;
}

// Below this magnitude (AED) a price delta is treated as float noise, not a
// real discount, so the Discounts section stays hidden.
const DISCOUNT_EPSILON = 0.01;

// Admission statuses that back a line the user actually pays for and that the
// payment summary renders as a `successPassengers` row. Mirrors the inclusion
// set used when building those rows in preparePaymentSummary.
const DISCOUNT_SUCCESS_STATUSES = new Set([
    "FULFILLED",
    "FULLFILLED", // backend typo, matched elsewhere in this file too
    "PREBOOKED",
    "EXCHANGED",
    "ON_HOLD",
]);

// Maps the passenger label returned by getUpperPassengerLabelByAge to the
// display suffix UpdatePaymentItemsAction uses on the `${count} ${label}`
// success-passenger rows, so a per-type discount can be matched back to its
// row. Only "Senior Citizen" differs ("(UAE)" suffix); the rest are identity.
const DISCOUNT_TYPE_DISPLAY_SUFFIX: Record<string, string> = {
    "Senior Citizen": "Senior Citizen (UAE)",
};

const readDiscountNameAndCode = (admission: any, offer: any): { name: string; code: string } => {
    // Promotion/reduction shapes vary between the offers response and the
    // booked-offer response, so probe the known locations defensively and
    // fall back to an empty name (the render shows no label — per product, no
    // generic fallback text).
    const promo = admission?.appliedPromotionCodes?.[0];
    const reduction =
        admission?.reductionSummary?.[0] ||
        offer?.reductionSummary?.[0] ||
        offer?.admissions?.[0]?.reductionSummary?.[0];
    const name = promo?.name || reduction?.name || "";
    const code =
        promo?.code ||
        (typeof promo === "string" ? promo : "") ||
        reduction?.code ||
        "";
    return { name, code };
};

// Some booked admissions don't carry the discount NAME or the pre-discount
// GROSS directly: booking-details strips `reductionSummary`, and the prebooked
// upgrade/change-date (exchange) admission carries neither `reductionSummary`
// nor `originalPrice` — both live only on the offers response
// (offerAtom / upgradeOutbound|InboundOffersAtom), on `admissionOfferParts`.
// Build a { passenger, net price } → { name, code, gross } index from those
// parts. Deep-walks to stay robust to the offer/exchange-offer response shape.
//
// Keyed on the PASSENGER as well as the price because net price alone is not
// unique across an offers response: a child fare is half an adult fare, so the
// AED 50 adult part of the 99 fare and the AED 50 child part of the 100 fare
// land on the same key — and last-write-wins handed the adult's saving the
// child part's name. That is how one 50%-off campaign on an adult + child
// surfaced as a single "Child Discount −74" line. `passengerRefs` on the offer
// part is the same externalRef the booking passenger carries, so it separates
// them.
//
// The passenger-less key is retained as a fallback: the upgrade/change-date
// exchange offers do not always carry passengerRefs on their parts, and that
// flow depends on this index for the gross as well as the name. It is populated
// ONLY by parts that name no passenger — writing every part to it would keep
// the original last-write-wins collision alive behind the fix, one miss away.
//
// A narrowing, not a proof: a response carrying both ref'd and ref-less parts
// still has a fallback bucket a missed lookup can reach, so the lookup site
// also rejects any match whose gross contradicts the fare in hand. Between the
// two, an unattributable fare degrades to NO name (the render shows an
// unlabelled line; per product there is no generic fallback text) rather than
// to another passenger's.
const ANY_PASSENGER = "";
const offerPartKey = (passengerRef: string, net: number) => `${passengerRef}|${Math.round(net * 100)}`;

const buildOfferDiscountIndex = (offers: any): Map<string, { name: string; code: string; gross: number }> => {
    const map = new Map<string, { name: string; code: string; gross: number }>();
    const parts: any[] = [];
    const collect = (node: any) => {
        if (!node || typeof node !== "object") return;
        if (Array.isArray(node)) { node.forEach(collect); return; }
        if (Array.isArray(node.admissionOfferParts)) parts.push(...node.admissionOfferParts);
        for (const key of Object.keys(node)) collect(node[key]);
    };
    collect(offers);
    for (const part of parts) {
        const net = calculatePrice(part?.price);
        if (!(net > 0)) continue;
        const gross = calculatePrice(part?.originalPrice);
        // `[0]` — the fare's headline reduction — deliberately, and matching
        // the mobile app. A child's part lists two (the age-based "Child
        // Discount" and the campaign on top of it) while the amount shown is
        // the whole originalPrice − price gap. Naming the line off the
        // delta-matching reduction instead was tried and reverted: it renamed
        // the child's line and "Child Discount" vanished from the summary.
        const reduction = part?.reductionSummary?.[0];
        if (!(reduction?.name || reduction?.code || gross > net)) continue;
        const entry = { name: reduction?.name || "", code: reduction?.code || "", gross };
        // Array.isArray, not a cast: a string here would make `.length` truthy
        // and `for...of` walk it character by character, minting a key per
        // letter. Same guard the shipped join in PaymentReceiptForUpgrade uses.
        const refs: string[] = (Array.isArray(part?.passengerRefs) ? part.passengerRefs : []).filter(Boolean);
        if (refs.length) {
            for (const ref of refs) map.set(offerPartKey(ref, net), entry);
        } else {
            map.set(offerPartKey(ANY_PASSENGER, net), entry);
        }
    }
    return map;
};

/**
 * Surface campaign/promo discounts in the payment + booking summary.
 *
 * The backend prices admissions at their NET (already-discounted) value and
 * exposes the pre-discount fare via `originalPrice`. The R4 FSD payment-summary
 * design itemizes those reductions in a dedicated "Discounts" section. This
 * step, per direction:
 *   1. derives each admission's discount (originalPrice - price) for the
 *      fulfilled/prebooked/exchanged admissions that back the displayed
 *      `successPassengers` rows,
 *   2. bumps those displayed rows up to GROSS,
 *   3. records the named discount line items on otherDetails.discounts.
 *
 * payment.total is deliberately NOT touched: gross rows minus the discount
 * rows reconcile back to the existing net total, so Total / refund / voucher
 * math downstream is byte-identical. The whole step is a no-op unless a real
 * reduction exists (originalPrice > price), so non-discounted bookings — and
 * every existing flow/test — are unaffected.
 *
 * Caller responsibility: invoke on the fare-paying checkout surfaces only —
 * normal booking, on-hold completion, upgrade-to-premium, and change-date.
 * Because the step is total-neutral and self-balancing (every amount added to a
 * row is subtracted back as a line item), those flows' gross/net deltas still
 * reconcile. Booking-details (read-only), manage-seating/addons and add-shuttle
 * (tickets already paid — discount belongs to the original purchase), plain
 * quick-exchange, and multibooking are excluded by the caller.
 */
export function applyNormalFlowDiscounts(
    booking: IBooking,
    allBookedOfferWithoutExchange: IBookedOffer[],
    allTripWithoutExchange: IBookingTypeTrip[],
    paymentSummary: any,
    offers?: any
): void {
    // Discount NAME and (for exchange/upgrade admissions) the pre-discount GROSS
    // come from the offers response — the booking API drops them. Matched to each
    // booked fare by its passenger AND its net price.
    const offerDiscountIndex = buildOfferDiscountIndex(offers);
    const passengerById = new Map<string, any>(
        (booking?.passengers || []).map((p) => [p.id, p] as const)
    );
    // Map every trip id to its direction. Seed from the full booking.trips
    // first (covers the original trips that back the EXCHANGE_ONGOING fares on
    // upgrade/change-date), then overlay allTripWithoutExchange so its
    // direction-recovered values win for the new prebooked trips.
    const directionByTripId = new Map<string, string>();
    const indexTrip = (t: any) =>
        directionByTripId.set(t.id, t.direction === TripTypeEnum.IN_BOUND ? TrainTypeEnum.inbound : TrainTypeEnum.outbound);
    (booking?.trips || []).forEach(indexTrip);
    (allTripWithoutExchange || []).forEach(indexTrip);

    const labelSuffixForPassenger = (passenger: any): string => {
        if (!passenger) return "";
        const label = getUpperPassengerLabelByAge(passenger.age || 0, {
            cards: passenger.cards || [],
            prmNeeds: passenger.prmNeeds || [],
            email: passenger?.detail?.email || "",
            firstName: "",
            id: passenger.id,
            lastName: "",
            mobile: "",
            age: passenger.age || 0,
            remember: false,
        } as any);
        return DISCOUNT_TYPE_DISPLAY_SUFFIX[label] || label;
    };

    // direction -> display suffix -> summed discount, for the Discounts lines
    const discountByDirectionType: Record<string, Record<string, number>> = {};
    // direction -> display suffix -> summed net of the fares being exchanged
    // away (EXCHANGE_ONGOING). On upgrade/change-date this is the already-paid
    // "Initial Fare"; on normal/on-hold flows there is none.
    const alreadyPaidByDirectionType: Record<string, Record<string, number>> = {};
    // global named buckets, for the itemized Discounts section
    const namedDiscounts: Record<string, { name: string; amount: number; code: string }> = {};
    let grandDiscountTotal = 0;

    // Already-paid "Initial Fare" = net of the fares being exchanged away
    // (EXCHANGE_ONGOING). On upgrade/change-date these live in the FULL
    // booking.bookedOffers on the original trips — allBookedOfferWithoutExchange
    // is filtered to the new prebooked premium fares, so they are read here.
    // Normal/on-hold bookings have no EXCHANGE_ONGOING admissions → no credit.
    for (const offer of booking?.bookedOffers || []) {
        if (offer?.products?.[0]?.code === SHUTTLE_PRODUCT_CODE) continue;
        const direction = directionByTripId.get(offer?.tripCoverage?.coveredTripId || "");
        if (!direction) continue;
        for (const admission of offer?.admissions || []) {
            if ((admission?.status || "").toUpperCase() !== "EXCHANGE_ONGOING") continue;
            const paid = calculatePrice(admission?.price);
            if (!(paid > DISCOUNT_EPSILON)) continue;
            const suffix = labelSuffixForPassenger(passengerById.get(admission?.passengerIds?.[0] || ""));
            if (!alreadyPaidByDirectionType[direction]) alreadyPaidByDirectionType[direction] = {};
            alreadyPaidByDirectionType[direction][suffix] =
                (alreadyPaidByDirectionType[direction][suffix] || 0) + paid;
        }
    }

    for (const offer of allBookedOfferWithoutExchange || []) {
        // Shuttle fares render as their own `shuttlePassengers` rows, not the
        // per-type `successPassengers` rows this step bumps to gross. Skip them
        // so a (hypothetical) shuttle reduction can't be mis-attributed onto a
        // train ticket row keyed by the same passenger type.
        if (offer?.products?.[0]?.code === SHUTTLE_PRODUCT_CODE) continue;
        const direction = directionByTripId.get(offer?.tripCoverage?.coveredTripId || "");
        if (!direction) continue;
        for (const admission of offer?.admissions || []) {
            const status = (admission?.status || "").toUpperCase();
            if (!DISCOUNT_SUCCESS_STATUSES.has(status)) continue;

            const passenger = passengerById.get(admission?.passengerIds?.[0] || "");
            const suffix = labelSuffixForPassenger(passenger);
            // An admission whose passenger is not on the booking cannot be
            // attributed to a row: the row-bump loop matches rows by this
            // suffix, so an empty one emits a subtracting line that nothing
            // above it accounts for and the column stops adding up.
            if (!suffix) continue;
            const net = calculatePrice(admission?.price);
            // Forward-booking admissions carry `originalPrice` directly; the
            // prebooked upgrade/change-date (exchange) admission doesn't — fall
            // back to the matched offer part's gross (admissionOfferParts.
            // originalPrice).
            const admissionGross = calculatePrice((admission as any)?.originalPrice);

            // This passenger's own part first, the passenger-less entry second —
            // and either is rejected when its gross CONTRADICTS the admission's
            // own, which proves they are different fare products that merely
            // share a net price. The check covers BOTH lookups: with no
            // `externalRef` the "specific" key IS the passenger-less key, so
            // guarding only the second would skip it exactly where attribution
            // is weakest.
            //
            // `> 0`, not `> net`: a full-fare admission (originalPrice === price)
            // is PROOF there is no reduction, and must not fall through to a
            // stranger's gross. No gross on either side stays permissive — the
            // upgrade shape, where this index is the only source of one.
            // DISCOUNT_EPSILON doubles as the same-fare tolerance here; one fils
            // is the smallest real price step.
            const match =
                offerDiscountIndex.get(offerPartKey(passenger?.externalRef || ANY_PASSENGER, net)) ||
                offerDiscountIndex.get(offerPartKey(ANY_PASSENGER, net));
            const contradicts =
                admissionGross > 0 &&
                (match?.gross || 0) > 0 &&
                Math.abs((match?.gross || 0) - admissionGross) > DISCOUNT_EPSILON;
            const offerMatch = contradicts ? undefined : match;

            const gross = admissionGross > net ? admissionGross : (offerMatch?.gross || 0);
            const discount = gross - net;
            if (!(discount > DISCOUNT_EPSILON)) continue;

            if (!discountByDirectionType[direction]) discountByDirectionType[direction] = {};
            discountByDirectionType[direction][suffix] =
                (discountByDirectionType[direction][suffix] || 0) + discount;

            // Prefer the offer-sourced name; fall back to whatever the booking
            // admission carries (usually nothing).
            const { name, code } = offerMatch || readDiscountNameAndCode(admission, offer);
            // ONE LINE PER PASSENGER TYPE. An adult's saving and a child's
            // saving are two different facts and the summary must not add them
            // together — a merged line reads as one passenger's discount and
            // matches no row above it. Same-type passengers still merge (two
            // adults on one promo = one line), and so do the two legs of a
            // round trip.
            const bucketKey = `${name || "__generic__"}|${code}|${suffix}`;
            if (!namedDiscounts[bucketKey]) namedDiscounts[bucketKey] = { name, amount: 0, code };
            namedDiscounts[bucketKey].amount += discount;
            grandDiscountTotal += discount;
        }
    }

    if (!(grandDiscountTotal > DISCOUNT_EPSILON)) return; // no real discount → leave summary untouched

    // 1) bump the displayed ticket rows to FULL gross. The bump is
    //    discount + already-paid: on a normal booking already-paid is 0 so the
    //    net row simply rises to gross; on an upgrade/change-date the row starts
    //    at the delta (new net − already-paid) and rises to the full new gross.
    //    payment.total is NOT touched (finalized before this step) — the
    //    Discounts and Initial Fare lines below subtract back down to it.
    let initialFareApplied = 0;
    for (const direction of [TrainTypeEnum.outbound, TrainTypeEnum.inbound] as string[]) {
        const disc = discountByDirectionType[direction] || {};
        const paid = alreadyPaidByDirectionType[direction] || {};
        const rows = paymentSummary?.[direction]?.passengerDetails?.successPassengers;
        if (!Array.isArray(rows)) continue;
        const suffixes = new Set([...Object.keys(disc), ...Object.keys(paid)]);
        for (const row of rows) {
            const suffix = [...suffixes].find((s) => s && (row?.description || "").endsWith(s));
            if (!suffix) continue;
            row.amount = (row.amount || 0) + (disc[suffix] || 0) + (paid[suffix] || 0);
            initialFareApplied += paid[suffix] || 0;
        }
    }

    if (!paymentSummary.otherDetails) paymentSummary.otherDetails = {};
    // 2) record the named discount line items (render shows the real name as-is;
    //    per product there is no generic fallback when name is empty)
    paymentSummary.otherDetails.discounts = Object.values(namedDiscounts)
        .filter((d) => d.amount > DISCOUNT_EPSILON)
        // The passenger type is a BUCKETING key only — it never reaches the
        // line. The discount's name is the backend's and is rendered verbatim,
        // so one campaign hitting two passenger types shows as two lines under
        // the same name, each carrying that type's own amount.
        .map((d) => ({ name: d.name, amount: d.amount, ...(d.code ? { code: d.code } : {}) }));
    // 3) Initial Fare credit (upgrade/change-date only): the already-paid net of
    //    the fares being exchanged, shown as a deduction above the gross rows so
    //    grossRows − InitialFare − Discounts reconciles to the (delta) Total.
    if (initialFareApplied > DISCOUNT_EPSILON) {
        paymentSummary.otherDetails.initialFare = initialFareApplied;
    }
}

class PrepareBookingSummaryAction extends BaseAction<void, IBooking & { isSplit?: boolean, isFrom?: string, onlyFulfillPax?: boolean, isChangeDate?: boolean, selectedPassengerForCancel?: string[], selectedPassengerForCancelInBound?: string[], isManageSeating?: boolean, isManageAddons?: boolean, changeTripAlreadyPaidAddons?: any, isUpgrade?: boolean, isPrebookedOnly?: boolean }> {

    private filterPrebookedOffersAndTrips(allBookedOfferWithoutExchange: IBookedOffer[], allTripWithoutExchange: IBookingTypeTrip[]) {
        // `?.` guards Nol offers, which carry no admissions array at all.
        // NOTE the guard EXCLUDES such an offer (undefined is falsy) rather
        // than throwing — see the matching note in updateLegTripSummary.
        // NOTE: onward shuttles are deliberately NOT excluded here. This helper
        // also feeds the isPrebookedOnly path (line 1885 / branch 2136) used by
        // the Add-shuttle-transfer flow, where the shuttle IS the offer being
        // charged. Excluding it there empties the set and the shuttle is billed
        // as nothing.
        const prebookedOffers = allBookedOfferWithoutExchange.filter(offer =>
            offer.admissions?.some(admission => admission.status === "PREBOOKED")
        );

        const prebookedTripIds = prebookedOffers.map(offer => offer.tripCoverage.coveredTripId);

        const prebookedTrips = allTripWithoutExchange.filter(trip => 
            prebookedTripIds.includes(trip.id)
        );

        return {
            prebookedOffers,
            prebookedTrips
        };
    }

    async updateLegTripSummary(legList: ILeg[], bookedOffers: IBookedOffer[], direction: TrainTypeEnum.outbound | TrainTypeEnum.inbound, legSummary = null, booking?: IBooking, isUpgrade: boolean = false, isPrebookedOnly: boolean = false) {

        let filteredBookedOffers = bookedOffers;
        if (isPrebookedOnly) {
            // `?.` because Nol offers carry no admissions array at all.
            // CONSEQUENCE, not just crash-safety: `undefined` is falsy, so an
            // admissions-less offer is EXCLUDED from the payable set rather
            // than throwing. For a PREBOOKED Nol that means billing it as
            // nothing — the same shape as the shuttle bug in §3 of the findings
            // doc. Unreachable today (this path is entered only by add-shuttle,
            // which adds no Nol); if a Nol ever reaches it, fall back to the
            // offer's `ancillaries` status instead of dropping the offer.
            filteredBookedOffers = bookedOffers.filter(offer =>
                offer.admissions?.some(admission => admission.status === "PREBOOKED")
            );
        }

        let params: IGetLegsFromTripActionParams = {
            trip: {},
            tripDirection: direction,
        }
        const letTrain = legList.find(leg => leg.timedLeg.service.mode.ptMode !== "BUS")
        if (letTrain) {
            const legOffer = filteredBookedOffers.find(offer => offer.tripCoverage.coveredLegIds.includes(letTrain.id))
            // const getTrainSummary = legOffer?.products[0].summary || ""
            // let priceClass = "premium"
            // if (getTrainSummary.includes("Premium Flexible")) {
            //     priceClass = "premium"
            // } else {
            //     if (getTrainSummary.includes("Standard Flexible")) {
            //         if (getTrainSummary.includes("with Fee")) {
            //             priceClass = "standardValue"
            //         } else {
            //             priceClass = "standardPro"
            //         }
            //     }
            // }
            // let priceClass = getTrainClassTypeBaseOnProduct(isUpgrade ? booking?.bookedOffers[0]?.products || [] : legOffer?.products || []) || ""
            let priceClass = getTrainClassTypeBaseOnProduct(legOffer?.products || []) || ""
            params = {
                trip: { legs: legList, priceClass: priceClass },
                tripDirection: direction,
            }
        }
        else {
            params = {
                trip: { legs: legList, priceClass: "" },
                tripDirection: direction
            }
        }
        if (legSummary) {
            params.trainLegsValue = legSummary
        }
        params.bookingData = booking
        params.bookedOffers = filteredBookedOffers as any[]
        // NOTE: a `skipStateUpdate` opt-out was tried here and REVERTED, twice.
        // It suppresses the selectedTrainLegsAtom write, and it is wrong for two
        // reasons: (a) `isPrebookedOnly` is ALSO set by onwardSummaryParams for
        // change-date, where the prebooked legs are the NEW TRAIN's and must be
        // written; (b) its stated beneficiary — a train-leg card on the
        // add-shuttle checkout — is moot, because that flow sets
        // isQuickExchangeStateAtom so onwardMode resolves to "hidden" there.
        // Attempt 4 in the findings doc tested it on INT: the section still did
        // not render. Do not re-add without live verification.
        getLegsSummaryFromTripAction.setParams(params)
        await getLegsSummaryFromTripAction.execute()

        if (getLegsSummaryFromTripAction.status.executionStatus === ExecutionStatus.SUCCESS) {
            const result = getLegsSummaryFromTripAction.result!
            return result
        }
    }
    preparePaymentSummary = async (booking: IBooking, allBookedOfferWithoutExchange: IBookedOffer[], allTripWithoutExchange: IBookingTypeTrip[], changeTripAlreadyPaidAddons: any, isSplit: boolean = false, onlyFulfillPax: boolean = false, filteredTrips?: any[], filteredBookedOffers?: any[], isUpgrade: boolean = false, isChangeDate = false, calculateAlreadyPaidAddons = false, isOnHoldBooking: boolean = false, isManageSeating = false, isManageAddons = false, isFrom: string = "") => {
        // Check if multi-booking at the start (default to false for regular bookings)
        const isMultiBooking = getRecoilStateValue<boolean>("isMultiBookingStateAtom", false)
        const multiBooking = isBookingMultiBooking({booking})
        
        calculatePassengerListByPriceAction.setParams({
            booking: booking,
            screen: "",
            passengerSpecificationsTypes: "",
            isSplit,
            onlyFulfillPax,
            filteredTrips,
            filteredBookedOffers
        })
        await calculatePassengerListByPriceAction.execute()

        // Prepare empty paymentSummary, but do not add outbound/inbound yet
        let paymentSummary: any = {
            otherDetails: {
                voucher: {},
                addons: {}
            },
            payment: {
                subTotal: 0,
                total: 0,
                refundedTotal: 0,
                refundedAmount: 0,
                vatPercentage: booking?.confirmedPrice?.vats?.[0]?.percentage ? booking?.confirmedPrice?.vats?.[0]?.percentage : booking?.provisionalPrice?.vats?.[0]?.percentage ? booking?.provisionalPrice?.vats?.[0]?.percentage : null,
                currency: booking?.confirmedPrice?.currency || booking?.provisionalPrice?.currency || "AED"
            }
        }

        if (calculatePassengerListByPriceAction.status.executionStatus === ExecutionStatus.SUCCESS) {
            const result = calculatePassengerListByPriceAction.result

            let successPassenger: any = []
            // A booking with no train at all is the standalone Shuttle flow, where
            // the refunded rider is already reported through refundPassengers.
            // Inlined rather than reusing Lib/Utils/booking's `hasTrainLeg`: six
            // existing suites mock that module with just the exports this file
            // used to need, and importing a third resolved `undefined` in every
            // one of them (164 failures). Two duplicated lines beat six mock edits.
            const bookingHasTrainLeg = (allTripWithoutExchange || []).some(t =>
                (t.legs || []).some(leg => leg.timedLeg?.service?.mode?.ptMode === PtModeEnum.TRAIN)
            )
            // Process each trip based on its direction
            for (let i = 0; i < allTripWithoutExchange.length; i++) {
                const trip = allTripWithoutExchange[i]
                const isOutbound = trip.direction === TripTypeEnum.OUT_BOUND
                const direction = isOutbound ? TrainTypeEnum.outbound : TrainTypeEnum.inbound

                // Only add the direction object if the trip exists
                if (!paymentSummary[direction]) {

                    paymentSummary[direction] = {
                        fromName: "",
                        toName: "",
                        priceClass: "",
                        passengerDetails: {
                            successPassengers: [],
                            shuttlePassengers: undefined,
                            appliedPass: [],
                            refundPassengers: [],
                        },
                        refundSummary: undefined,
                        [`${direction}SuccessTotalPrice`]: 0,
                        [`${direction}TotalPrice`]: 0,
                        [`${direction}RefundedTotalPrice`]: 0
                    }
                }

                const checkTripHaveShuttleLeg = trip.legs.filter(leg => leg.timedLeg.service.mode.ptMode === "BUS")
                let checkTripHaveTrainLeg = trip.legs.find(leg => leg.timedLeg.service.mode.ptMode === "TRAIN")
                let priceClass = "not_found"
                let productName = ""
                if (booking?.exchangeOperations) {
                    const allExchangeOffers: any[] = []
                    const allExchangeTrips: any[] = []
                    booking.exchangeOperations.forEach((exchangeOp: any) => {
                        if (exchangeOp?.exchangeOffers && Array.isArray(exchangeOp.exchangeOffers)) {
                            allExchangeOffers.push(...exchangeOp.exchangeOffers)
                        }
                        if (exchangeOp?.trips && Array.isArray(exchangeOp.trips)) {
                            allExchangeTrips.push(...exchangeOp.trips)
                        }
                    })
                    // Try matching exchange offer by trip ID first
                    let matchingExchangeOffer = allExchangeOffers.find(offer => offer?.tripCoverage?.coveredTripId === trip.id)
                    // Fallback: match by direction through exchange trips
                    if (!matchingExchangeOffer) {
                        const directionTripIds = new Set(
                            allExchangeTrips.filter(t => t?.direction === trip.direction).map(t => t?.id).filter(Boolean)
                        )
                        matchingExchangeOffer = allExchangeOffers.find(offer =>
                            offer?.tripCoverage?.coveredTripId && directionTripIds.has(offer.tripCoverage.coveredTripId)
                        )
                    }
                    if (matchingExchangeOffer) {
                        priceClass = getTrainClassTypeBaseOnProduct(matchingExchangeOffer?.products || []) || ""
                        const exchangeAdmissionProduct = matchingExchangeOffer?.products?.find((p: any) => p.type === ProductTypeEnum.ADMISSION_POINT2POINT) || matchingExchangeOffer?.products?.[0]
                        // Booking surfaces show the catalog product name verbatim ("Comfort
                        // Flex") — class-prefix stripping is for search fare cards only,
                        // where the fare sits under a class-labeled column.
                        productName = exchangeAdmissionProduct?.summary || exchangeAdmissionProduct?.description || ""
                    }
                }

                if (checkTripHaveTrainLeg) {
                    const trainOffer: IBookedOffer | undefined = allBookedOfferWithoutExchange.find(offer => offer.tripCoverage.coveredTripId === trip.id && offer.tripCoverage.coveredLegIds.includes(checkTripHaveTrainLeg.id))
                    if (trainOffer) {
                        priceClass = getTrainClassTypeBaseOnProduct(trainOffer.products || []) || ""
                        const admissionProduct = trainOffer.products?.find(p => p.type === ProductTypeEnum.ADMISSION_POINT2POINT) || trainOffer.products?.[0]
                        productName = admissionProduct?.summary || admissionProduct?.description || ""
                    }
                } else {
                    priceClass = "" // "" means shuttle
                    // productName intentionally left "" for shuttle-only trips:
                    // shuttle legs have no ADMISSION_POINT2POINT product to display.
                }

                // Only update priceClass if this trip has a train leg, or if not meaningfully set yet
                if (checkTripHaveTrainLeg || !paymentSummary[direction].priceClass) {
                    paymentSummary[direction].priceClass = priceClass
                    paymentSummary[direction].productName = productName
                }

                if (isMultiBooking || multiBooking) {
                    if (!paymentSummary[direction].fromName) {
                        paymentSummary[direction].fromName = trip.legs[0].timedLeg.start?.stopPlaceRef?.stopPlaceRef || ""
                    }
                    if (!paymentSummary[direction].toName) {
                        paymentSummary[direction].toName = trip.legs[trip.legs.length - 1].timedLeg.end?.stopPlaceRef?.stopPlaceRef || ""
                    }
                } else {
                    // Only set the main route header from train trips (shuttle routes are shown per shuttle entry)
                    if (checkTripHaveTrainLeg) {
                        paymentSummary[direction].fromName = trip.legs[0].timedLeg.start?.stopPlaceRef?.stopPlaceRef || ""
                        paymentSummary[direction].toName = trip.legs[trip.legs.length - 1].timedLeg.end?.stopPlaceRef?.stopPlaceRef || ""
                    }
                }

                // The direction's TRAIN stations, from TRAIN legs only — never the
                // route header above.
                //
                // On a Train+Shuttle trip the header spans the whole journey, and
                // `legs[0]` there is the BUS leg, so the header's own start is the
                // SHUTTLE'S OWN STOP. A shuttle row names whichever of its two ends
                // is not a station, so a header-derived list marks that stop as a
                // station and the row confidently names the other end — the train
                // station, the one name the row exists to avoid.
                //
                // Every train leg's endpoints, not just the trip's outer pair: a
                // connecting journey changes trains at an intermediate station, and
                // a shuttle can meet the passenger there. Unioned across the
                // direction's trips because an onward-travel shuttle is its own
                // train-less trip alongside the train one, and it needs that trip's
                // stations to identify its own stop.
                const trainLegs = trip.legs.filter((leg: any) => leg.timedLeg.service.mode.ptMode === "TRAIN")
                if (trainLegs.length > 0) {
                    const seen = new Set(paymentSummary[direction].stationRefs || [])
                    trainLegs.forEach((leg: any) => {
                        const start = leg.timedLeg.start?.stopPlaceRef?.stopPlaceRef
                        const end = leg.timedLeg.end?.stopPlaceRef?.stopPlaceRef
                        if (start) seen.add(start)
                        if (end) seen.add(end)
                    })
                    paymentSummary[direction].stationRefs = Array.from(seen)
                }

                const currentTripOffers = allBookedOfferWithoutExchange.filter(offer => 
                    offer.tripCoverage.coveredTripId === trip.id
                )
                
                let currentTripTotalFromOffers = 0
                let currentTripRefundTotalFromOffers = 0
                if (isMultiBooking || multiBooking) {
                    currentTripOffers.forEach(offer => {
                        offer.admissions.forEach(admission => {
                            if (admission.status === "FULFILLED" || admission.status === "PREBOOKED" || admission.status === "EXCHANGED" || admission.status === "ON_HOLD") {
                                currentTripTotalFromOffers += calculatePrice(admission.price)
                            }
                            if (admission.status === "REFUNDED" || admission.status === "RELEASED") {
                                currentTripRefundTotalFromOffers += calculatePrice(admission.price)
                            }
                        })
                    })
                }

                // const allTheFulFillmentPassengers = result?.[isOutbound ? "outBoundPassengerPriceList" : "inBoundPassengerPriceList"]?.filter((d: any) => d.pStatus === "FULFILLED" || d.pStatus === "PREBOOKED" || d.pStatus === "EXCHANGED") || []
                const allTheFulFillmentPassengers =
                    result?.[isOutbound ? "outBoundPassengerPriceList" : "inBoundPassengerPriceList"]?.filter((d: any) =>
                        onlyFulfillPax
                            ? d.pStatus === "FULFILLED" || d.pStatus === "PREBOOKED"
                            : d.pStatus === "FULFILLED" || d.pStatus === "PREBOOKED" || d.pStatus === "EXCHANGED" || d.pStatus === "ON_HOLD"
                    ) || [];
                const allTheRefundedPassengers = result?.[isOutbound ? "outBoundPassengerPriceList" : "inBoundPassengerPriceList"]?.filter((d: any) => d.pStatus === "REFUNDED" || d.pStatus === "RELEASED") || []

                await updatePaymentItemsAction.setParams({
                    atomKey: isOutbound ? "paymentOutboundAtom" : "paymentInboundAtom",
                    outBoundPassengerPriceList: allTheFulFillmentPassengers
                })
                await updatePaymentItemsAction.execute()

                if (isActionRunSuccessfully(updatePaymentItemsAction)) {
                    successPassenger = [...updatePaymentItemsAction.result || []]
                    if (checkTripHaveShuttleLeg.length > 0) {
                        const passengerList =
                            result?.[isOutbound ? "outBoundPassengerPriceList" : "inBoundPassengerPriceList"]
                                ?.filter((d: any) =>
                                    onlyFulfillPax
                                        ? d.pStatus === "FULFILLED" || d.pStatus === "PREBOOKED"
                                        : isOnHoldBooking
                                            ? d.pStatus === "FULFILLED" || d.pStatus === "PREBOOKED" || d.pStatus === "EXCHANGED" || d.pStatus === "ON_HOLD"
                                            : d.pStatus === "FULFILLED" || d.pStatus === "PREBOOKED" || d.pStatus === "EXCHANGED"
                                ) || [];

                        // ALL offers covering this leg, not `.find`'s first match: a
                        // trip can carry more than one (the per-trip accumulator ~60
                        // lines up already assumes that), and first-match meant a
                        // refunded offer listed ahead of a live one hid the ticket the
                        // customer actually holds — invisible AND missing from the total.
                        const shuttleOffers = allBookedOfferWithoutExchange.filter(offer => offer.tripCoverage.coveredLegIds.includes(checkTripHaveShuttleLeg[0].id))
                        const allShuttleAdmissions = shuttleOffers.flatMap(offer => offer.admissions || [])
                        // Only riders still being carried. A shuttle refunded by a
                        // change-date (or released) keeps its offer + trip on the
                        // booking, so reading every admission gave every past shuttle
                        // its own permanent "1 Shuttle Bus" line in the summary — five
                        // add/cancel rounds, five lines. The booking-details
                        // all-PREBOOKED filter drops never-paid offers (checkout
                        // surfaces keep them, by design); nothing dropped
                        // paid-then-refunded ones. Terminal, not "not owned":
                        // PREBOOKED/ON_HOLD riders are unpaid but still on the offer
                        // and the add-shuttle checkout must price them.
                        const liveShuttleAdmissions = allShuttleAdmissions.filter(
                            admission => !TERMINAL_ADMISSION_STATUSES.has(admission?.status || "")
                        )
                        const shuttlePassengerIds = liveShuttleAdmissions.flatMap(admission => admission.passengerIds || [])

                        const shuttleFrom = checkTripHaveShuttleLeg[0].timedLeg.start?.stopPlaceRef?.stopPlaceRef || ""
                        const shuttleTo = checkTripHaveShuttleLeg[checkTripHaveShuttleLeg.length - 1].timedLeg.end?.stopPlaceRef?.stopPlaceRef || ""
                        const pushShuttleRow = (row: { description: string, count: number, amount: number, isRefunded?: boolean }) => {
                            if (!paymentSummary[direction].passengerDetails.shuttlePassengers) {
                                paymentSummary[direction].passengerDetails.shuttlePassengers = []
                            }
                            paymentSummary[direction].passengerDetails.shuttlePassengers.push({
                                ...row,
                                fromName: shuttleFrom,
                                toName: shuttleTo
                            })
                        }

                        // Nothing carried on this shuttle → no LIVE line, and skip the
                        // fallback below: with no live admissions it has no ids to
                        // match on, so it would substitute the whole train party for
                        // a row that must not be charged. The refunded row below still
                        // renders — the customer paid for it once.
                        if (shuttleOffers.length > 0 && liveShuttleAdmissions.length > 0) {
                            let shuttlePassengersList = passengerList.filter((p: any) => shuttlePassengerIds.includes(p.pId))

                            // ponytail: when live admissions exist but none of their
                            // passengerIds match the direction's list, this substitutes
                            // the whole party — so `description` counts everyone while
                            // `amount` now prices only the live admissions ("2 Shuttle —
                            // AED 10"). The count was already a guess here; the fix
                            // makes the PRICE right, which is the number the customer is
                            // charged, so the residual is cosmetic. Reconcile the two if
                            // a real booking ever shows the mismatched label.
                            // (An extra `!passengerList.some(p => ids.includes(p.pId))`
                            // guard stood here; it is implied by the empty filter above.)
                            if (passengerList.length > 0 && shuttlePassengersList.length === 0) {
                                shuttlePassengersList = passengerList
                            }
                            if (shuttlePassengersList.length > 0) {
                                // Sum all admission prices returned by the API instead of multiplying a single admission
                                const relevantAdmissions = isOnHoldBooking
                                    ? (liveShuttleAdmissions.filter(admission => admission.status === "ON_HOLD").length > 0
                                        ? liveShuttleAdmissions.filter(admission => admission.status === "ON_HOLD")
                                        : liveShuttleAdmissions)
                                    : liveShuttleAdmissions
                                const totalShuttlePrice = relevantAdmissions.reduce((sum, admission) => sum + calculatePrice(admission.price), 0)
                                pushShuttleRow({
                                    description: `${shuttlePassengersList.length} Shuttle`,
                                    count: shuttlePassengersList.length,
                                    amount: totalShuttlePrice
                                })
                            }
                        }

                        // The refunded half of the same offer. A change-date refunds
                        // only the MOVING passenger's admission, so a split party
                        // lands here AND above — one live line and one struck-through
                        // line, which is what actually happened.
                        // Counted from admissions (one per rider) rather than matched
                        // against passengerList: a refunded rider is absent from that
                        // list, so matching would always yield zero.
                        //
                        // ONE ROW PER STOP, not per trip and not one row for the lot.
                        // Every past add/cancel round is its own trip and so its own
                        // iteration, and each row names its own stop — "5 Shuttle
                        // Corniche ADNOC HQ (Cancelled)".
                        //
                        // Merging everything into one line (the previous behaviour) had
                        // to pick a single stop name for the lot, so a customer who
                        // cancelled shuttles to two different stops read the wrong one.
                        // Merging nothing gave that same customer five identical lines
                        // for five retries to the SAME stop — the noise the old collapse
                        // existed to remove. Keying on the stop pair is what both of
                        // those were reaching for: distinct stops stay distinct, repeats
                        // of one stop read as the one fact they are.
                        //
                        // booking-details ONLY, and never on a shuttle-only trip:
                        //  - a checkout summary answers "what am I paying now", so
                        //    refund history there is clutter, not record — and it would
                        //    put the five dead rows straight back on the surface this
                        //    branch set out to clear.
                        //  - a shuttle-only BOOKING already renders its cancelled rider
                        //    through refundPassengers below (the !checkTripHaveTrainLeg
                        //    branch), so adding this row lists the same refund twice.
                        //    Keyed off the booking, not this trip: an onward-travel
                        //    shuttle is its own train-less trip inside a train booking,
                        //    and that is precisely the case this row exists for.
                        const refundedShuttleAdmissions = isFrom === "booking-details" && bookingHasTrainLeg
                            ? allShuttleAdmissions.filter(
                                admission => REFUNDED_ADMISSION_STATUSES.has(admission?.status || "")
                            )
                            : []
                        if (refundedShuttleAdmissions.length > 0) {
                            const refundedCount = refundedShuttleAdmissions.length
                            const refundedAmount = refundedShuttleAdmissions.reduce((sum, admission) => sum + calculatePrice(admission.price), 0)
                            const rows = paymentSummary[direction].passengerDetails.shuttlePassengers || []
                            // Same stop pair = same shuttle, cancelled more than once.
                            // Matched on the refs rather than the resolved names: the
                            // names are a render-time concern and two refs that resolve
                            // to the same string are still two different stops.
                            const existing = rows.find((row: any) =>
                                row.isRefunded && row.fromName === shuttleFrom && row.toName === shuttleTo
                            )
                            if (existing) {
                                const runningCount = (existing.count || 0) + refundedCount
                                paymentSummary[direction].passengerDetails.shuttlePassengers = rows.map((row: any) =>
                                    row === existing
                                        ? {
                                            ...row,
                                            description: `${runningCount} Shuttle`,
                                            count: runningCount,
                                            amount: row.amount + refundedAmount
                                        }
                                        : row
                                )
                            } else {
                                pushShuttleRow({
                                    description: `${refundedCount} Shuttle`,
                                    count: refundedCount,
                                    amount: refundedAmount,
                                    isRefunded: true
                                })
                            }
                        }
                    }

                    if (result?.allPassenger?.[direction]?.length) {
                        const passengerWithPass = result?.allPassenger[direction].filter(p => p.passAmount && p.passType)
                        paymentSummary[direction].passengerDetails.appliedPass = passengerWithPass.map(p => ({
                            passType: p.passType,
                            passengerType: allTheFulFillmentPassengers?.find((i: any) => i.pId === p.id)?.pTypeValue,
                            amount: p.passAmount
                        }))
                    }

                    if (checkTripHaveShuttleLeg.length !== trip.legs.length) {
                        paymentSummary[direction].passengerDetails.successPassengers = successPassenger || []
                        if (booking?.exchangeOperations && booking?.exchangeOperations?.length > 0) {
                            const tripDirection = direction == TrainTypeEnum.inbound ? TripTypeEnum.IN_BOUND : TripTypeEnum.OUT_BOUND;
                            const allTrips = booking.trips?.filter(x => x.direction == tripDirection);
                            let allOffers = booking.bookedOffers?.filter(x => x.admissions[0]?.status === "EXCHANGE_ONGOING")?.filter(x => allTrips?.map(x => x.id)?.includes(x.tripCoverage.coveredTripId)) || []

                            if (isUpgrade && allOffers && allOffers.length > 1) {
                                const isShuttleOffer = (offer: IBookedOffer) => {
                                    return (offer.products || []).some((p: any) => 
                                        p.summary?.toLowerCase().includes("shuttle") || 
                                        p.code?.toLowerCase().includes("sht")
                                    );
                                };
                                
                                const nonShuttleOffer = allOffers.find(offer => !isShuttleOffer(offer));
                                
                                allOffers = nonShuttleOffer ? [nonShuttleOffer] : [allOffers[0]];
                            }

                            // booking.bookedOffers?.forEach((offer: IBookedOffer) => {
                            allOffers.forEach((offer: IBookedOffer) => {
                                const offerAdmissions = offer.admissions
                                const isAnyFulfilleds = offerAdmissions.filter(d => d.status === "EXCHANGE_ONGOING")
                                if (isAnyFulfilleds.length > 0) {
                                    isAnyFulfilleds.forEach(isAnyFulfilled => {
                                        const passenger = booking.passengers.find(p => isAnyFulfilled.passengerIds.includes(p.id))
                                        if (passenger) {
                                            const passengerAlreadyPaid = calculatePrice(isAnyFulfilled.price)
                                            const passengerAgeGroup = getUpperPassengerLabelByAge(0, {
                                                cards: passenger.cards,
                                                prmNeeds: passenger.prmNeeds,
                                                email: passenger.detail.email,
                                                firstName: "",
                                                id: passenger.id,
                                                lastName: "",
                                                mobile: "",
                                                age: passenger.age,
                                                remember: false,
                                            })
                                            successPassenger = successPassenger.map((sp: any) => {
                                                if (sp.description.includes(passengerAgeGroup)) {
                                                    const amount = parseFloat(sp?.amount || 0) - passengerAlreadyPaid
                                                    return {
                                                        ...sp,
                                                        amount: amount > 0 ? amount : 0
                                                    }
                                                }
                                                return sp;
                                            })
                                        }
                                    })
                                    paymentSummary[direction].passengerDetails.exchangeSuccessPassengers = successPassenger || []
                                }
                            })
                        } else {
                            paymentSummary[direction].passengerDetails.exchangeSuccessPassengers = null
                        }
                    }

                    await updatePaymentItemsAction.setParams({
                        atomKey: isOutbound ? "paymentOutBoundRefundItemsDetails" : "paymentInBoundRefundItemsDetails",
                        outBoundPassengerPriceList: allTheRefundedPassengers
                    })
                    await updatePaymentItemsAction.execute()

                    if (isActionRunSuccessfully(updatePaymentItemsAction)) {
                        let refundPassengers = updatePaymentItemsAction.result || []

                        // Relabel the refunded riders as "<n> Shuttle" only on the
                        // standalone shuttle booking. `allTheRefundedPassengers` is the
                        // whole DIRECTION's refunded list, not this trip's, and the
                        // assignment below is direction-level too — so keying this off
                        // the trip alone meant a shuttle trip sitting beside a train
                        // trip overwrote the train's "1 Adult" refund row with a
                        // "1 Shuttle" one carrying the adult's fare.
                        if (checkTripHaveShuttleLeg.length > 0 && !checkTripHaveTrainLeg && !bookingHasTrainLeg) {
                            const totalRefundCount = (allTheRefundedPassengers || []).length
                            const totalRefundAmount = (refundPassengers || []).reduce((sum: number, obj: any) => sum + (obj?.amount || 0), 0)
                            refundPassengers = totalRefundCount > 0 ? [{ description: `${totalRefundCount} Shuttle`, amount: totalRefundAmount }] : []
                        }

                        paymentSummary[direction].passengerDetails.refundPassengers = refundPassengers

                        // Calculate totals
                        const shouldUseMultiTotals = (isMultiBooking || multiBooking) && (currentTripTotalFromOffers > 0 || currentTripRefundTotalFromOffers > 0)
                        if (shouldUseMultiTotals) {
                            if (currentTripTotalFromOffers > 0) {
                                // For multi-booking, calculate total from offers and accumulate across trips
                            const currentTripPassesTotal = paymentSummary[direction].passengerDetails.appliedPass.reduce((sum: number, obj: any) => sum + (obj?.amount || 0), 0)
                            const currentTripShuttlePassengerTotal = (paymentSummary[direction].passengerDetails.shuttlePassengers || []).filter((s: any) => !s.isRefunded).reduce((sum: number, s: any) => sum + (s.amount || 0), 0)
                            const currentTripTotal = currentTripTotalFromOffers + currentTripShuttlePassengerTotal - currentTripPassesTotal
                            
                            paymentSummary[direction][`${direction}SuccessTotalPrice`] = (paymentSummary[direction][`${direction}SuccessTotalPrice`] || 0) + currentTripTotal
                            
                            const currentTripRefundedPassengerTotal = (refundPassengers || []).reduce((sum: number, obj: any) => sum + (obj?.amount || 0), 0)
                            paymentSummary[direction][`${direction}RefundedTotalPrice`] = (paymentSummary[direction][`${direction}RefundedTotalPrice`] || 0) + currentTripRefundedPassengerTotal
                            }


                            if (currentTripRefundTotalFromOffers > 0) {
                                const existingSummary = paymentSummary[direction].refundSummary || { canceledTripCount: 0, canceledTripTotal: 0 }
                                paymentSummary[direction].refundSummary = {
                                    canceledTripCount: existingSummary.canceledTripCount + 1,
                                    canceledTripTotal: existingSummary.canceledTripTotal + currentTripRefundTotalFromOffers
                                }
                            }
                        } else {
                            // For regular booking, use original logic (overwrite, not accumulate)
                            const successPassengerTotal = paymentSummary[direction].passengerDetails.successPassengers.reduce((sum: number, obj: any) => sum + obj.amount, 0)
                            const passesTotal = paymentSummary[direction].passengerDetails.appliedPass.reduce((sum: number, obj: any) => sum + obj.amount, 0)
                            const shuttlePassengerTotal = (paymentSummary[direction].passengerDetails.shuttlePassengers || []).filter((s: any) => !s.isRefunded).reduce((sum: number, s: any) => sum + (s.amount || 0), 0)
                            paymentSummary[direction][`${direction}SuccessTotalPrice`] = successPassengerTotal + shuttlePassengerTotal - passesTotal

                            const refundedPassengerTotal = paymentSummary[direction].passengerDetails.refundPassengers.reduce((sum: number, obj: any) => sum + obj.amount, 0)
                            // Refunded shuttles belong here too. The receipt's Sub Total is
                            // `total + refundedTotal` and its Total is that minus
                            // `payment.refundedAmount` — and refundedAmount sums every
                            // offer's admission.refundAmount, shuttles included. Leaving
                            // them out under-reported Sub Total by the shuttle fare and
                            // left Total negative, visible only because the receipt
                            // clamps it at 0.
                            const refundedShuttleTotal = (paymentSummary[direction].passengerDetails.shuttlePassengers || []).filter((s: any) => s.isRefunded).reduce((sum: number, s: any) => sum + (s.amount || 0), 0)
                            paymentSummary[direction][`${direction}RefundedTotalPrice`] = refundedPassengerTotal + refundedShuttleTotal
                        }
                    }
                }
            }

            // Calculate final totals
            paymentSummary.payment.total = (paymentSummary.outbound?.outboundSuccessTotalPrice || 0) + (paymentSummary.inbound?.inboundSuccessTotalPrice || 0)
            paymentSummary.payment.refundedTotal = (paymentSummary.outbound?.outboundRefundedTotalPrice || 0) + (paymentSummary.inbound?.inboundRefundedTotalPrice || 0)

            if (isMultiBooking || multiBooking) {
                const outboundTripIds = new Set(
                    allTripWithoutExchange
                        .filter(trip => trip.direction === TripTypeEnum.OUT_BOUND)
                        .map(trip => trip.id)
                )
                const inboundTripIds = new Set(
                    allTripWithoutExchange
                        .filter(trip => trip.direction === TripTypeEnum.IN_BOUND)
                        .map(trip => trip.id)
                )

                if (paymentSummary.outbound) {
                    paymentSummary.outbound.tripCount = outboundTripIds.size
                    paymentSummary.outbound.tripTotal = paymentSummary.outbound.outboundSuccessTotalPrice || 0
                }
                if (paymentSummary.inbound) {
                    paymentSummary.inbound.tripCount = inboundTripIds.size
                    paymentSummary.inbound.tripTotal = paymentSummary.inbound.inboundSuccessTotalPrice || 0
                }
            }

            // Checking if booking is pass booking
            const passObjectType = booking.bookedOffers[0]?.admissions?.[0]?.indicatedConsumption?.unit?.objectType || ""
            if (passObjectType) {
                const passObjectTranslationKey = passObjectType === "DayTravelAccountUnit" ? "offer.monthlyPassTitle" : "offer.travelPassTitle"

                const passPrice = booking.bookedOffers.map(offer => offer.admissions.map(admission => calculatePrice(admission.price))).flat().reduce((sum, price) => sum + price, 0)

                paymentSummary.otherDetails.offerPass = {
                    description: passObjectTranslationKey,
                    amount: passPrice
                }

                paymentSummary.payment.total = paymentSummary.payment.total + passPrice
            }

            // Handle exchange operations
            if (booking?.exchangeOperations && booking?.exchangeOperations?.length > 0) {
                const allExchangeOffers: any[] = [];
                const allTrips: any[] = [];
                
                booking.exchangeOperations.forEach((exchangeOp: any) => {
                    if (exchangeOp?.exchangeOffers && Array.isArray(exchangeOp.exchangeOffers)) {
                        allExchangeOffers.push(...exchangeOp.exchangeOffers);
                    }
                    if (exchangeOp?.trips && Array.isArray(exchangeOp.trips)) {
                        allTrips.push(...exchangeOp.trips);
                    }
                });

                const uniqueTripIds = new Set(allTrips.map(trip => trip?.id).filter(Boolean));
                const uniqueTrips = Array.from(uniqueTripIds).map(id => 
                    allTrips.find(trip => trip?.id === id)
                ).filter(Boolean);

                // Store OUTBOUND FEE
                // Guard against APIs returning "OUTBOUND" (TripDirectionTypeEnum) instead of "OUT_BOUND" (TripTypeEnum).
                const outboundTrips = uniqueTrips.filter(trip => isOutbound(trip?.direction));
                if (outboundTrips.length > 0 && paymentSummary.outbound) {
                    const outboundTripIds = new Set(outboundTrips.map(trip => trip?.id).filter(Boolean));
                    const outboundOffers: any[] = allExchangeOffers.filter(
                        offer => offer?.tripCoverage?.coveredTripId && 
                                outboundTripIds.has(offer.tripCoverage.coveredTripId) && 
                                offer?.exchangeFee
                    );

                    if (outboundOffers.length > 0) {
                        const totalExchangeFee = outboundOffers.reduce((sum, offer) => {
                            return sum + calculatePrice(offer.exchangeFee);
                        }, 0);
                        paymentSummary.outbound.passengerDetails.exchangeFee = totalExchangeFee;
                    }
                }

                // Store INBOUND FEE
                // Guard against APIs returning "INBOUND" (TripDirectionTypeEnum) instead of "IN_BOUND" (TripTypeEnum).
                const inboundTrips = uniqueTrips.filter(trip => isInbound(trip?.direction));
                if (inboundTrips.length > 0 && paymentSummary.inbound) {
                    const inboundTripIds = new Set(inboundTrips.map(trip => trip?.id).filter(Boolean));
                    const inboundOffers: any[] = allExchangeOffers.filter(
                        offer => offer?.tripCoverage?.coveredTripId && 
                                inboundTripIds.has(offer.tripCoverage.coveredTripId) && 
                                offer?.exchangeFee
                    );

                    if (inboundOffers.length > 0) {
                        const totalExchangeFee = inboundOffers.reduce((sum, offer) => {
                            return sum + calculatePrice(offer.exchangeFee);
                        }, 0);
                        paymentSummary.inbound.passengerDetails.exchangeFee = totalExchangeFee;
                    }
                }

                let alreadyPaid: number = 0

                if (isUpgrade) {
                    // Only compute alreadyPaid for directions present in allTripWithoutExchange.
                    // When only one direction is being upgraded (C2/C3 branch), including both
                    // directions would overcount alreadyPaid and make total negative (shows 0).
                    // Use isOutbound/isInbound so Transcity-style "OUTBOUND"/"INBOUND" matches the
                    // same way as exchange-fee logic above; otherwise alreadyPaid stays 0 while
                    // exchangeOperations exist and the delta fallback (below) is skipped.
                    const upgradeDirections = new Set<string>(
                        (allTripWithoutExchange || [])
                            .map((t: any) => t.direction)
                            .filter(Boolean)
                    );
                    const shouldIncludeOutbound =
                        upgradeDirections.size === 0 ||
                        [...upgradeDirections].some((d) => isOutbound(d));
                    const shouldIncludeInbound =
                        upgradeDirections.size === 0 ||
                        [...upgradeDirections].some((d) => isInbound(d));

                    if (shouldIncludeOutbound) {
                        const outboundTrip = booking.trips?.find(trip => isOutbound(trip.direction));
                        if (outboundTrip) {
                            // Get all outbound offers (including shuttle + train)
                            const outboundOffers = booking.bookedOffers?.filter(offer =>
                                offer.tripCoverage.coveredTripId === outboundTrip.id
                            ) || [];
                            // Process all outbound offers
                            outboundOffers.forEach(outboundOffer => {
                                const offerAdmissions = outboundOffer.admissions;
                                const isAnyFulfilleds = offerAdmissions.filter(d => d.status === "EXCHANGE_ONGOING");
                                if (isAnyFulfilleds.length > 0) {
                                    isAnyFulfilleds.forEach(isAnyFulfilled => {
                                        alreadyPaid = alreadyPaid + calculatePrice(isAnyFulfilled.price);
                                    });
                                }
                            });
                        }
                    }

                    if (shouldIncludeInbound) {
                        const inboundTrip = booking.trips?.find(trip => isInbound(trip.direction));
                        if (inboundTrip) {
                            // Get all inbound offers (including shuttle + train)
                            const inboundOffers = booking.bookedOffers?.filter(offer =>
                                offer.tripCoverage.coveredTripId === inboundTrip.id
                            ) || [];
                            // Process all inbound offers
                            inboundOffers.forEach(inboundOffer => {
                                const offerAdmissions = inboundOffer.admissions;
                                const isAnyFulfilleds = offerAdmissions.filter(d => d.status === "EXCHANGE_ONGOING");
                                if (isAnyFulfilleds.length > 0) {
                                    isAnyFulfilleds.forEach(isAnyFulfilled => {
                                        alreadyPaid = alreadyPaid + calculatePrice(isAnyFulfilled.price);
                                    });
                                }
                            });
                        }
                    }
                } else {
                    booking.bookedOffers?.forEach((offer: IBookedOffer) => {
                        const offerAdmissions = offer.admissions
                        const isAnyFulfilleds = offerAdmissions.filter(d => d.status === "EXCHANGE_ONGOING")
                        if (isAnyFulfilleds.length > 0) {
                            isAnyFulfilleds.forEach(isAnyFulfilled => {
                                alreadyPaid = alreadyPaid + calculatePrice(isAnyFulfilled.price)
                            })
                        }
                    })
                }

                if (alreadyPaid > 0) {
                    paymentSummary.payment.alreadyPaid = alreadyPaid
                }
                paymentSummary.payment.subTotal = (paymentSummary.payment.total + (paymentSummary.outbound?.passengerDetails?.exchangeFee || 0) + (paymentSummary.inbound?.passengerDetails?.exchangeFee || 0)) || 0
                const rawTotalAfterAlreadyPaid = paymentSummary.payment.subTotal - (paymentSummary.payment.alreadyPaid || 0)
                // Upgrade golden rule: no refund. Clamp so excess alreadyPaid is forfeited
                // rather than refunded. Other exchange flows (CDT, quick-exchange) keep the
                // raw value so their existing refund/voucher logic continues to work.
                paymentSummary.payment.total = isUpgrade ? Math.max(0, rawTotalAfterAlreadyPaid) : rawTotalAfterAlreadyPaid

                if (paymentSummary[TrainTypeEnum.outbound]) {
                    if (paymentSummary[TrainTypeEnum.outbound]?.passengerDetails?.exchangeSuccessPassengers) {
                        paymentSummary[TrainTypeEnum.outbound].passengerDetails.successPassengers = paymentSummary[TrainTypeEnum.outbound]?.passengerDetails?.exchangeSuccessPassengers
                    }
                }
                if (paymentSummary[TrainTypeEnum.inbound]) {
                    if (paymentSummary[TrainTypeEnum.inbound]?.passengerDetails?.exchangeSuccessPassengers) {
                        paymentSummary[TrainTypeEnum.inbound].passengerDetails.successPassengers = paymentSummary[TrainTypeEnum.inbound]?.passengerDetails?.exchangeSuccessPassengers
                    }
                }
            }

            // NOTE: this walks offers with no admission-status filter, so a
            // SALES_FEE sitting on a REFUNDED shuttle offer would reach the Total
            // even though its row does not (only all-PREBOOKED offers are dropped,
            // and only EXCHANGED is stripped upstream — REFUNDED survives both).
            // Checked against the live payload for the reported booking: every
            // shuttle offer has `fees: []`, so nothing leaks there today. Add
            // `.filter(o => o.admissions.some(a => !TERMINAL_ADMISSION_STATUSES.has(a.status)))`
            // here if a booking ever turns up carrying one.
            const shouldIncludeSalesFee = isOnHoldBooking || isFrom === "booking-details";
            if (shouldIncludeSalesFee) {
                let bookingFeeTotal = 0;
                let bookingFeeLabel = "";

                (allBookedOfferWithoutExchange || []).forEach((offer: IBookedOffer) => {
                    const fees = (offer as any)?.fees || [];
                    if (Array.isArray(fees)) {
                        fees.forEach((fee: any) => {
                            if (fee?.type === "SALES_FEE" && fee?.price) {
                                bookingFeeLabel = fee.summary;
                                bookingFeeTotal += calculatePrice(fee.price);
                            }
                        });
                    }
                });

                if (bookingFeeTotal > 0) {
                    paymentSummary.otherDetails.bookingFee = {
                        description: bookingFeeLabel,
                        amount: bookingFeeTotal
                    };
                    paymentSummary.payment.total = (paymentSummary.payment.total || 0) + bookingFeeTotal;
                }
            }

            // New logic Added: Sum refundAmount from admissions in allBookedOfferWithoutExchange (not per direction) TO SHOW THE REFUNDED AMOUNT COMING FROM API.
            let refundedAmount = 0;
            allBookedOfferWithoutExchange.forEach(offer => {
                offer.admissions.forEach(admission => {
                    const refundAmount = (admission as any)?.refundAmount;
                    if (refundAmount && admission?.status !== 'EXCHANGED') {
                        refundedAmount += calculatePrice(refundAmount);
                    }
                });
            });
            paymentSummary.payment.refundedAmount = refundedAmount;

            /**
             * Manage Addons into booking for booking summary on checkout page.
             * On CDT checkout page no need to show already selected addons.
             * On Quick exchange checkout page no need to show already selected addons.             
             * On Upgrade To Premium checkout page no need to show already selected addons.
             * On Book It Again checkout page no need to show already selected addons.
            */
            if (!calculateAlreadyPaidAddons || isManageSeating) {
                // Track minPassengerIdx per category so the emitted list mirrors
                // booking.passengers order (adult → senior → …). Ancillaries
                // arrive in arbitrary backend order; without this, the Saver
                // Tickets block above shows "1 Adult, 1 Senior citizen" while
                // the addon rows below appear in whatever order the backend
                // returned them.
                const passengerIdxById = new Map<string, number>(
                    (booking?.passengers || []).map((p, idx) => [p.id, idx] as const)
                )
                // pendingRefund buckets carry refundFee + refundableAmount
                // (summed from the per-ancillary probe cache) so the rendered
                // line item can show the NET refund and a hover tooltip with
                // the gross/fee breakdown. active/canceled buckets ignore
                // these fields.
                type Bucket = {
                    amount: number;
                    count: number;
                    minPassengerIdx: number;
                    refundFee?: number;
                    refundableAmount?: number;
                    // Tracks the FULFILLED portion within the same category so
                    // manage-seating / manage-addons can suppress pure-FULFILLED
                    // line items from the displayed addons section while still
                    // accounting for them in the totalPrice math (subTotal +
                    // alreadyPaid invariants in the override block at line ~1014).
                    fulfilledAmount?: number;
                    fulfilledCount?: number;
                }
                const addonsAccumulator: Record<string, {
                    active: Record<string, Bucket>,
                    canceled: Record<string, Bucket>,
                    pendingRefund: Record<string, Bucket>
                }> = {}

                // Manage-seating queues FULFILLED ancillaries the user has
                // deselected into pendingRefundFulfillmentIdsAtom; the actual
                // backend status flips to REFUNDED only after payment success.
                // To present these as outgoing in the summary (rather than
                // hidden in the active bucket and inflating the total),
                // bucket them as pendingRefund here.
                const pendingRefundQueue =
                    (getRecoilStateValue("pendingRefundFulfillmentIdsAtom") as
                        { bookingId: string; fulfillmentIds: string[] }
                        | null) || null
                const pendingRefundIds = new Set<string>(
                    pendingRefundQueue?.bookingId === booking?.id
                        ? pendingRefundQueue?.fulfillmentIds || []
                        : []
                )

                // Per-ancillary refund quotes from the speculative probe cache
                // (populated by ProbeRefundOffersAction at manage-seating
                // entry). Stamped onto each pendingRefund line item so
                // PaymentReceipt's AddonLineItem can render a sibling
                // "Refund fee" row right below each refund row.
                const refundQuoteCache = getRecoilStateValue(
                    "refundOffersByFulfillmentIdAtom",
                ) as {
                    bookingId: string;
                    entries: Record<string, { refundFee: number; refundableAmount: number }>
                } | null
                const quoteForBooking =
                    refundQuoteCache?.bookingId === booking?.id ? refundQuoteCache : null

                allBookedOfferWithoutExchange?.forEach((bookedOffer: IBookedOffer) => {
                    const bookTrip = allTripWithoutExchange?.find(d => d.id === bookedOffer.tripCoverage.coveredTripId)
                    if (!bookTrip) {
                        return
                    }

                    const direction = (bookTrip.direction === TripTypeEnum.IN_BOUND ? TrainTypeEnum.inbound : TrainTypeEnum.outbound) as string
                    const allAncillaryForTrip = bookedOffer?.ancillaries?.filter(d => d?.tripCoverage?.coveredTripId === bookTrip?.id)

                    if (!addonsAccumulator[direction]) {
                        addonsAccumulator[direction] = {
                            active: {},
                            canceled: {},
                            pendingRefund: {}
                        }
                    }

                    for (const ancillaries of allAncillaryForTrip) {
                        // On the booking-details page (read-only view of a
                        // confirmed booking), PREBOOKED ancillaries are
                        // orphans from a prior abandoned manage-seating /
                        // addons session. Don't include them in the booking
                        // summary — they aren't part of what the user paid
                        // for. cleanupOrphanAddonsAction will purge them on
                        // the next flow entry; here we just hide them.
                        if (
                            isFrom === "booking-details" &&
                            ancillaries.status === "PREBOOKED"
                        ) {
                            continue
                        }
                        const productId = ancillaries.products[0].productId
                        const productDetails = bookedOffer?.products?.find(d => d.id === productId)
                        const category = productDetails?.description || ""
                        const ancId = (ancillaries as any)?.id || ""
                        const isPendingRefund = ancId && pendingRefundIds.has(ancId)
                        const statusKey: "active" | "canceled" | "pendingRefund" =
                            isPendingRefund
                                ? "pendingRefund"
                                : ancillaries.status === "REFUNDED"
                                    ? "canceled"
                                    : "active"
                        const target = addonsAccumulator[direction][statusKey]
                        const ancPassengerId: string | undefined = (ancillaries as any)?.passengerIds?.[0]
                        const idx = ancPassengerId !== undefined ? passengerIdxById.get(ancPassengerId) : undefined
                        const passengerIdx = typeof idx === "number" ? idx : Number.MAX_SAFE_INTEGER

                        if (!target[category]) {
                            target[category] = { amount: 0, count: 0, minPassengerIdx: passengerIdx, fulfilledAmount: 0, fulfilledCount: 0 }
                        } else {
                            target[category].minPassengerIdx = Math.min(target[category].minPassengerIdx, passengerIdx)
                        }

                        const ancPrice = calculatePrice(ancillaries.price)
                        target[category].amount += ancPrice
                        // `FULLFILLED_TYPO` matches the backend's misspelled
                        // status — the canonical `FULFILLED` lives in the
                        // enum but legacy responses occasionally ship the
                        // typo. Matched the same way as the `hasSettledItems`
                        // check (admission + ancillary + reservation
                        // FULFILLED/FULLFILLED scan) and the seat-fee
                        // reservation skip below.
                        const FULLFILLED_TYPO = "FULLFILLED"
                        const isFulfilledAncillary =
                            ancillaries.status === AncillaryStatusEnum.FULFILLED ||
                            ancillaries.status === FULLFILLED_TYPO
                        if (statusKey === "active" && isFulfilledAncillary) {
                            target[category].fulfilledAmount = (target[category].fulfilledAmount || 0) + ancPrice
                            target[category].fulfilledCount = (target[category].fulfilledCount || 0) + 1
                        }
                        target[category].count += 1

                        // For pendingRefund buckets, accumulate the per-ancillary
                        // refund quote so the rendered row can show the net
                        // refund and a hover-breakdown of the fee, plus a
                        // sibling "Refund fee" row right below it.
                        if (isPendingRefund && quoteForBooking) {
                            const entry = quoteForBooking.entries?.[ancId]
                            if (entry) {
                                target[category].refundFee =
                                    (target[category].refundFee || 0) + (entry.refundFee || 0)
                                target[category].refundableAmount =
                                    (target[category].refundableAmount || 0) + (entry.refundableAmount || 0)
                            }
                        }
                    }
                })

                const sortByPassengerOrder = (entries: Array<[string, Bucket]>): Array<[string, Bucket]> =>
                    [...entries].sort(([, a], [, b]) => a.minPassengerIdx - b.minPassengerIdx)

                Object.keys(addonsAccumulator)?.forEach(direction => {
                    const { active, canceled, pendingRefund } = addonsAccumulator[direction]

                    // Carries the per-category FULFILLED portion (amount +
                    // count) AND the raw category label / count so the
                    // display-time filter below can:
                    //   - suppress pure-FULFILLED rows entirely, OR
                    //   - reduce a mixed-category row to its PREBOOKED
                    //     portion (both amount AND count), so the visible
                    //     row reads "1 Meal 30 AED" instead of the
                    //     misleading "2 Meal 30 AED" when 1 of 2 was paid.
                    // The unfiltered list (with v.amount + v.count) is still
                    // used for the totalPrice sum below so subTotal and
                    // alreadyPaid stay consistent with the override block at
                    // line ~1014.
                    type DisplayedAddonRow = {
                        amount: number;
                        description: string;
                        fulfilledAmount: number;
                        fulfilledCount: number;
                        category: string;
                        count: number;
                    };

                    const finalActiveAddons: DisplayedAddonRow[] = sortByPassengerOrder(Object.entries(active)).map(([category, v]) => ({
                        amount: v.amount,
                        description: `${v.count} ${category}`,
                        fulfilledAmount: v.fulfilledAmount || 0,
                        fulfilledCount: v.fulfilledCount || 0,
                        category,
                        count: v.count,
                    }))

                    // Historical (already-REFUNDED) ancillary rows belong only
                    // on booking-details — they're a record of a past
                    // transaction. On checkout / manage-seating they confuse
                    // the live "To Refund" tag from the current session.
                    // Suppression is display-only: this bucket is NOT in the
                    // totalPrice sum below, so hiding it doesn't shift any
                    // math.
                    const finalCanceledAddons = isFrom === "booking-details"
                        ? sortByPassengerOrder(Object.entries(canceled)).map(([category, v]) => ({
                            amount: v.amount,
                            description: `${v.count} ${category} (Canceled)`,
                        }))
                        : []

                    const finalPendingRefundAddons = sortByPassengerOrder(Object.entries(pendingRefund)).map(([category, v]) => ({
                        amount: v.amount,
                        description: `${v.count} ${category} (Refund)`,
                        // Carries the per-line refund quote so AddonLineItem
                        // can render the NET refund instead of the gross
                        // price, and surface the gross/fee breakdown on hover.
                        refundFee: v.refundFee,
                        refundableAmount: v.refundableAmount,
                    }))

                    // Display-time filter for manage-seating / manage-addons:
                    // strip pure-FULFILLED rows from what the user sees in the
                    // addons section so the displayed line items match the
                    // amount being charged (the override block at line ~1014
                    // sets payment.total to PREBOOKED-only). A category that
                    // mixes FULFILLED + PREBOOKED keeps its row but BOTH the
                    // displayed amount AND the count label are reduced to the
                    // PREBOOKED portion (avoids "2 Meal ø 30" when one of two
                    // is already paid). The unfiltered `finalActiveAddons` is
                    // still used for the totalPrice math below so subTotal /
                    // alreadyPaid stay consistent with the override.
                    //
                    // Single-pass `flatMap` over `.map(...).filter(...)`:
                    //   - returns `[a]` to keep the row,
                    //   - returns `[transformedRow]` to keep a reduced row,
                    //   - returns `[]` to drop the row.
                    // Avoids the intermediate null-bearing array.
                    const displayedActiveAddons: DisplayedAddonRow[] = (isManageSeating || isManageAddons)
                        ? finalActiveAddons.flatMap((a) => {
                            // `<= 0` (rather than `=== 0`) is defensive against
                            // any future code path that lets `fulfilledAmount`
                            // arrive as a vanishingly-small negative from FP
                            // subtraction. Today the accumulator only adds, so
                            // this can never go negative; the guard is forward-
                            // compatible insurance, not a live concern.
                            if (a.fulfilledAmount <= 0) return [a]
                            const remaining = a.amount - a.fulfilledAmount
                            // Pure-FULFILLED category — every ancillary in the
                            // bucket was already paid for; drop the row.
                            if (remaining <= 0) return []
                            // Mixed FULFILLED + PREBOOKED. Reduce both the
                            // displayed amount AND the count so the row reads
                            // sensibly (e.g. "1 Meal ø 10" instead of "2 Meal
                            // ø 10" when 1 of 2 was already paid). The
                            // `Math.max(1, ...)` is a clamp belt-and-braces —
                            // remaining > 0 with count - fulfilledCount <= 0
                            // shouldn't happen because every contribution to
                            // fulfilledCount also contributes to count, but
                            // the clamp prevents a nonsense "0 Meal" label if
                            // that invariant ever broke.
                            const remainingCount = Math.max(1, a.count - a.fulfilledCount)
                            return [{ ...a, amount: remaining, description: `${remainingCount} ${a.category}` }]
                        })
                        : finalActiveAddons

                    const finalAddons = [...displayedActiveAddons, ...finalCanceledAddons, ...finalPendingRefundAddons]
                    paymentSummary.otherDetails.addons[direction] = finalAddons
                    // Include pending-refund amounts in the running total so
                    // the booking-level alreadyPaid credit (computed below
                    // from confirmedPrice) — which still includes those
                    // ancillaries because their backend status is FULFILLED
                    // until post-payment — cancels them out cleanly. Net
                    // effect: the user sees the GROSS total of new extras
                    // and the refund happens out-of-band post-payment, with
                    // no checkout-time offset.
                    //
                    // Uses the UNFILTERED finalActiveAddons (including
                    // FULFILLED portions) so payment.total represents the
                    // true gross — the manage-seating/manage-addons override
                    // block at line ~1014 subtracts the FULFILLED portion to
                    // arrive at the PREBOOKED-only amount due.
                    const totalPrice = [...finalActiveAddons, ...finalPendingRefundAddons]
                        .reduce((price, item) => price += item.amount, 0)
                    paymentSummary.payment.total = (paymentSummary?.payment?.total || 0) + totalPrice
                })
            }

            /**
             * Seat reservation fees — shown on regular checkout.
             * Excluded on CDT / Quick Exchange / Upgrade / Book It Again (calculateAlreadyPaidAddons = true).
             * Excluded on manage-seating (calculateAlreadyPaidAddons = true, zeroed separately).
             */
            if (!calculateAlreadyPaidAddons || isManageSeating) {
                // `hidden` flags categories that should be summed into
                // seatFeesTotal (so payment.total stays consistent with the
                // alreadyPaid credit derived from confirmedPrice) but
                // suppressed from the visible breakdown — used for historical
                // REFUND_FEE rows on non-booking-details views.
                const seatFeesAccumulator: Record<string, Record<string, { amount: number; count: number; hidden?: boolean }>> = {}
                // Also capture seat-related fees from offer.fees (e.g. PLACE_FEE, SEAT_FEE)
                // Skip types already handled elsewhere (SALES_FEE, MODIFICATION_FEE, EXCHANGE_FEE)
                const handledFeeTypes = new Set(["SALES_FEE", "MODIFICATION_FEE", "EXCHANGE_FEE"])

                allBookedOfferWithoutExchange?.forEach((bookedOffer: IBookedOffer) => {
                    const bookTrip = allTripWithoutExchange?.find(d => d.id === bookedOffer.tripCoverage.coveredTripId)
                    if (!bookTrip) return

                    const direction = (bookTrip.direction === TripTypeEnum.IN_BOUND ? TrainTypeEnum.inbound : TrainTypeEnum.outbound) as string

                    if (!seatFeesAccumulator[direction]) {
                        seatFeesAccumulator[direction] = {}
                    }

                    for (const reservation of (bookedOffer.reservations || [])) {
                        const status = ((reservation as any)?.status || "").toUpperCase()
                        // REFUNDED reservations must never count toward any total — skip unconditionally.
                        if (status === "REFUNDED") continue
                        // FULFILLED reservations are already-paid seats; skip in regular checkout
                        // but include in manage-seating so the user sees what they already hold.
                        if ((status === "FULFILLED" || status === "FULLFILLED") && !isManageSeating) continue

                        const price = calculatePrice(reservation.price)
                        if (price <= 0) continue

                        const productId = reservation.products?.[0]?.productId
                        const productDetails = (bookedOffer?.products as any[])?.find((d: any) => d.id === productId)
                        const category = productDetails?.description || "Seat"

                        if (!seatFeesAccumulator[direction][category]) {
                            seatFeesAccumulator[direction][category] = { amount: 0, count: 0 }
                        }
                        seatFeesAccumulator[direction][category].amount += price
                        seatFeesAccumulator[direction][category].count += 1
                    }

                    for (const fee of ((bookedOffer as any)?.fees || [])) {
                        if (!fee?.price) continue
                        if (handledFeeTypes.has(fee?.type)) continue
                        // Exchange/modification fees are already shown as the
                        // "Modification fee" line (passengerDetails.exchangeFee).
                        // Some backends tag them with a non-EXCHANGE_FEE type but
                        // an "Exchange fee"/"Modification fee" summary, which would
                        // otherwise leak in here as a duplicate "1 Exchange fee"
                        // line and double-count the total. Mirror the summary-based
                        // exclusion already used in mergeUpgradePaymentSummary.
                        if ((fee?.summary || "").toLowerCase().includes("exchange")) continue
                        if ((fee?.summary || "").toLowerCase().includes("modification")) continue

                        const feePrice = calculatePrice(fee.price)
                        if (feePrice <= 0) continue

                        const category = (fee.summary || fee.description || fee.type || "Seat Fee").replace(/\s*\(\w+,\s*\d+\)\s*/g, '').trim()
                        // Historical refund-fee rows (from a previous refund
                        // transaction) should stay in the running total but
                        // not surface as a visible breakdown row on checkout
                        // / manage-seating — they confuse the live "Refund
                        // fee" computed from the probe cache. Tracked via the
                        // `hidden` flag below so seatFeesTotal still picks
                        // them up.
                        const isHistoricalRefundFee =
                            fee?.type === "REFUND_FEE" ||
                            (fee?.summary || "").toLowerCase().includes("refund fee")
                        const shouldHide = isHistoricalRefundFee && isFrom !== "booking-details"
                        if (!seatFeesAccumulator[direction][category]) {
                            seatFeesAccumulator[direction][category] = {
                                amount: 0,
                                count: 0,
                                hidden: shouldHide,
                            }
                        } else if (!shouldHide) {
                            // a non-hidden fee with the same category un-hides it
                            seatFeesAccumulator[direction][category].hidden = false
                        }
                        seatFeesAccumulator[direction][category].amount += feePrice
                        seatFeesAccumulator[direction][category].count += 1
                    }
                })

                let seatFeesTotal = 0
                const seatFeesResult: Record<string, { amount: number; description: string }[]> = {}

                Object.keys(seatFeesAccumulator).forEach(direction => {
                    const entries = seatFeesAccumulator[direction]
                    // Visible breakdown skips `hidden` categories (historical
                    // refund-fee rows on non-booking-details views).
                    seatFeesResult[direction] = Object.keys(entries)
                        .filter(category => !entries[category].hidden)
                        .map(category => ({
                            amount: entries[category].amount,
                            description: `${entries[category].count} ${category}`,
                        }))
                    // Total still picks up hidden amounts so payment.total
                    // stays consistent with the alreadyPaid credit derived
                    // from confirmedPrice (which already accounts for the
                    // historical fee).
                    seatFeesTotal += Object.keys(entries)
                        .reduce((sum, cat) => sum + entries[cat].amount, 0)
                })

                if (seatFeesTotal > 0) {
                    paymentSummary.otherDetails.seatFees = seatFeesResult
                    paymentSummary.payment.total = (paymentSummary.payment.total || 0) + seatFeesTotal
                }
            }

            // In manage-seating / manage-addons, the customer pays only for newly-added
            // (PREBOOKED) items. FULFILLED items are already paid; pendingRefund items
            // settle out-of-band post-payment via the refund-via-session pipeline. The
            // upstream accumulators (tickets, FULFILLED addons, seat fees) leave payment.total
            // as a gross-of-everything number useful only as a subtotal/breakdown — override
            // it with a direct PREBOOKED sum so the customer is never re-charged for items
            // they already hold. PREBOOKED admissions (e.g. a ticket bought in the same
            // session, or a newly-prebooked seat) must still be charged — only FULFILLED
            // admissions are excluded.
            // Guard: only override on a booking that already has settled items — i.e. a
            // genuine "manage addons/seating on a confirmed booking" scenario. The seat-
            // selection refresh on a brand-new checkout also passes isManageAddons=true
            // (legacy), but there nothing is FULFILLED yet and payment.total already holds
            // the correct gross amount the customer must pay (ticket + new seat). Zeroing
            // it there is the bug behind "Adult 59 / Total 0".
            const hasSettledItems = (allBookedOfferWithoutExchange || []).some(offer =>
                (offer?.admissions || []).some(a => a?.status === "FULFILLED" || a?.status === "FULLFILLED")
                || ((offer as any)?.ancillaries || []).some((a: any) => a?.status === "FULFILLED" || a?.status === "FULLFILLED")
                || (offer?.reservations || []).some(r => r?.status === "FULFILLED" || r?.status === "FULLFILLED")
            ) || (!!booking?.confirmedPrice && calculatePrice(booking.confirmedPrice) > 0);

            if ((isManageAddons || isManageSeating) && hasSettledItems) {
                let prebookedTotal = 0;
                for (const offer of allBookedOfferWithoutExchange || []) {
                    for (const adm of (offer?.admissions || [])) {
                        if (adm?.status === "PREBOOKED") {
                            prebookedTotal += calculatePrice(adm.price);
                        }
                    }
                    for (const anc of ((offer as any)?.ancillaries || [])) {
                        if (anc?.status === AncillaryStatusEnum.PREBOOKED) {
                            prebookedTotal += calculatePrice(anc.price);
                        }
                    }
                }
                const grossBefore = paymentSummary.payment.total || 0;
                paymentSummary.payment.subTotal = grossBefore;
                paymentSummary.payment.alreadyPaid = Math.max(0, grossBefore - prebookedTotal);
                paymentSummary.payment.total = prebookedTotal;
                paymentSummary.payment.refundedAmount = 0;
                paymentSummary.otherDetails = {
                    ...(paymentSummary.otherDetails || {}),
                    isRefund: false,
                };
            }

            // Upgrade-to-Premium delta fallback: when the exchangeOperations block above did not
            // fire (booking.exchangeOperations absent or empty in the GET /booking response),
            // alreadyPaid was never set. Derive it from EXCHANGE_ONGOING admissions directly —
            // these always exist after postPreExchangeOperation, regardless of exchangeOperations.
            // Mirrors the manage-addons/manage-seating delta pattern, but enforces the upgrade
            // golden rule: NO refund. If alreadyPaid exceeds the upgrade total, clamp the payable
            // to 0 and forfeit the excess — never set refundedAmount or isRefund: true here.
            // Guard: skip fallback when exchangeOperations ARE present — the exchange ops block
            // above already ran with direction-scoped alreadyPaid computation. Running the
            // unscoped fallback would incorrectly include EXCHANGE_ONGOING from directions that
            // are NOT being upgraded (e.g. inbound on an outbound-only upgrade).
            if (isUpgrade && !paymentSummary.payment.alreadyPaid && !((booking?.exchangeOperations?.length ?? 0) > 0)) {
                let alreadyPaidFromExchange = 0;
                booking.bookedOffers?.forEach((offer: IBookedOffer) => {
                    offer.admissions
                        .filter(d => d.status === "EXCHANGE_ONGOING")
                        .forEach(admission => {
                            alreadyPaidFromExchange += calculatePrice(admission.price);
                        });
                });
                if (alreadyPaidFromExchange > 0) {
                    paymentSummary.payment.alreadyPaid = alreadyPaidFromExchange;
                    paymentSummary.payment.subTotal = paymentSummary.payment.total;
                    // Upgrade golden rule: no refund. If alreadyPaid > total, clamp to 0
                    // and forfeit the excess — never set refundedAmount/isRefund here.
                    paymentSummary.payment.total = Math.max(0, paymentSummary.payment.total - alreadyPaidFromExchange);
                    paymentSummary.otherDetails = {
                        ...(paymentSummary.otherDetails || {}),
                        isRefund: false,
                    };
                }
            }

            // Handle vouchers
            if (booking.pendingVouchers.length > 0) {
                const calculateVoucherAmount = booking.pendingVouchers.map(v => {
                    return {
                        amount: calculatePrice({ amount: v.amount.amount, scale: v.amount.scale, currency: "", vats: [] })
                    }
                }).reduce((sum: number, obj: any) => sum + obj.amount, 0)

                const voucher = {
                    amount: calculateVoucherAmount
                }
                paymentSummary.otherDetails.voucher = voucher
                if (isChangeDate) {
                    // Change-date: do NOT override payment.total with
                    // `provisionalPrice` (used to be the line below). The
                    // breakdown rendered in the UI — admission diff per
                    // passenger, seat fees / exchange fee, addons net of
                    // carry-over (handled in ConfirmAddonsAction) — is the
                    // source of truth for Total. Capture subTotal so a
                    // later voucher render still has the pre-voucher value.
                    paymentSummary.payment.subTotal = paymentSummary.payment.total
                } else if (paymentSummary.payment.total >= calculateVoucherAmount) {
                    paymentSummary.payment.subTotal = paymentSummary.payment.total
                    paymentSummary.payment.total = paymentSummary.payment.total - calculateVoucherAmount
                }
            } else {
                paymentSummary.otherDetails.voucher = {}
            }

            // Change-date: previously did `payment.total = calculatePrice(booking.provisionalPrice)`
            // here. Removed — Total comes from the breakdown (admission
            // diffs + seat/exchange fees + addons net of credit), never
            // from a single backend field that doesn't itself surface as a
            // visible row.
            //
            // CDT (live change-date) Total math: per-leg formula, replacing
            // whatever the upstream accumulators set.
            //
            //   For each direction d ∈ { outbound, inbound }:
            //     leg_total(d) = max(0, tickets(d) + addons(d) + fees(d) − credit(d))
            //   payment.total = leg_total(outbound) + leg_total(inbound)
            //
            // Where:
            //   tickets(d) = sum of `successPassengers[].amount` (per-passenger
            //                admission diff).
            //   addons(d)  = sum of active (non-canceled, non-pending-refund)
            //                amounts in `otherDetails.addons[d]`.
            //   fees(d)    = passengerDetails[d].exchangeFee.
            //   credit(d)  = `changeTripAlreadyPaidAddonsAtom[d]` — the user's
            //                prepaid ancillary value for that direction. Falls
            //                back to a live walk over FULFILLED +
            //                EXCHANGE_ONGOING ancillaries if the atom is empty.
            //
            // Per-leg credit offsets the leg's new spend including the fee
            // (pinned by PR #54 R1/R3/R4). Leftover credit on a leg doesn't
            // bleed into the other direction.
            //
            // QE (quick-exchange / prev-next train) is intentionally NOT in
            // this branch. QE has no "credit" concept: addons aren't refunded
            // and re-purchased — they're carried over silently to the new
            // exchange offer by `preSelectAddonsForExchangeAction`. The user
            // owes only the admission delta (0 for same-tier) plus the
            // modification fee. See the QE-specific branch below.
            const isCdtForTotal = isChangeDate && isLiveChangeDateCheckout();
            if (isCdtForTotal) {
                const sumPassengerAmounts = (passengers: any[] | undefined): number =>
                    (passengers || []).reduce((sum, p: any) => sum + (p?.amount || 0), 0);

                const sumActiveAddonItems = (
                    items: Array<{ amount?: number; description?: string }> | undefined,
                ): number =>
                    (items || []).reduce((sum, item) => {
                        const desc = item?.description || "";
                        if (desc.includes("(Canceled)") || desc.includes("(Refund)")) {
                            return sum;
                        }
                        return sum + (item?.amount || 0);
                    }, 0);

                // Atom-sourced credit per direction; live fallback if both
                // atom slots are empty.
                const creditAtom = getRecoilStateValue("changeTripAlreadyPaidAddonsAtom") as
                    | { outbound?: number; inbound?: number }
                    | null
                    | undefined;
                let creditOut = creditAtom?.outbound || 0;
                let creditIn = creditAtom?.inbound || 0;
                if (creditOut === 0 && creditIn === 0) {
                    // Flatten once: post-exchange the NOL product an ancillary
                    // references can live on a sibling offer, not just the offer
                    // that owns it.
                    const allProducts = ((booking?.bookedOffers || []) as IBookedOffer[]).flatMap((o) => (o as any)?.products || []);
                    for (const offer of (booking?.bookedOffers || []) as IBookedOffer[]) {
                        const tripCovered = (offer as any)?.tripCoverage?.coveredTripId;
                        const trip = (booking?.trips || []).find((t: any) => t?.id === tripCovered);
                        const dirKey =
                            trip?.direction === TripTypeEnum.IN_BOUND
                                ? TrainTypeEnum.inbound
                                : TrainTypeEnum.outbound;
                        for (const anc of (((offer as any)?.ancillaries || []) as any[])) {
                            // Exclude NOL day-passes here too: this live fallback
                            // fires precisely when the atom credit is 0 (which is
                            // what the NOL exclusion in CalculateAlreadyPaidAddons
                            // produces), so without this guard the fallback re-adds
                            // the NOL the atom path deliberately dropped. See
                            // [[isNolAncillary]].
                            if (isNolAncillary(allProducts, anc)) continue;
                            if (
                                anc?.status === AncillaryStatusEnum.FULFILLED ||
                                anc?.status === AncillaryStatusEnum.EXCHANGE_ONGOING
                            ) {
                                if (dirKey === TrainTypeEnum.outbound) {
                                    creditOut += calculatePrice(anc?.price);
                                } else {
                                    creditIn += calculatePrice(anc?.price);
                                }
                            }
                        }
                    }
                }

                const ticketsOut = sumPassengerAmounts(
                    (paymentSummary as any)?.[TrainTypeEnum.outbound]?.passengerDetails?.successPassengers,
                );
                const ticketsIn = sumPassengerAmounts(
                    (paymentSummary as any)?.[TrainTypeEnum.inbound]?.passengerDetails?.successPassengers,
                );
                const addonsOut = sumActiveAddonItems(
                    paymentSummary?.otherDetails?.addons?.[TrainTypeEnum.outbound],
                );
                const addonsIn = sumActiveAddonItems(
                    paymentSummary?.otherDetails?.addons?.[TrainTypeEnum.inbound],
                );
                const feesOut =
                    (paymentSummary as any)?.[TrainTypeEnum.outbound]?.passengerDetails?.exchangeFee || 0;
                const feesIn =
                    (paymentSummary as any)?.[TrainTypeEnum.inbound]?.passengerDetails?.exchangeFee || 0;

                const legTotalOut = Math.max(0, ticketsOut + addonsOut + feesOut - creditOut);
                const legTotalIn = Math.max(0, ticketsIn + addonsIn + feesIn - creditIn);
                const newTotal = legTotalOut + legTotalIn;
                const grossCredit = creditOut + creditIn;

                paymentSummary.payment.subTotal =
                    ticketsOut + ticketsIn + addonsOut + addonsIn + feesOut + feesIn;
                paymentSummary.payment.total = newTotal;
                if (grossCredit > 0) {
                    paymentSummary.payment.alreadyPaidAddonsAmount = grossCredit;
                }
            }

            // QE (quick-exchange / prev-next train) Total math: simple sum.
            //
            //   payment.total = max(0, tickets(out)) + max(0, tickets(in))
            //                 + fees(out) + fees(in)
            //
            // No credit, no addon offset. Addons aren't refunded — they're
            // attached to the new PREBOOKED exchange offer by
            // `preSelectAddonsForExchangeAction` and travel along with the
            // booking to the new departure. The only money owed is the
            // admission delta (zero for same-tier exchanges, positive for
            // upgrades, clamped to zero for the rare downgrade case so the
            // user doesn't get refunded a price difference they aren't
            // entitled to) plus the modification fee.
            //
            // CRITICAL — strict flow gating. `isQuickExchangeStateAtom` is
            // overloaded: manage-seating, manage-addons, upgrade-to-premium,
            // add-shuttle, on-hold-booking, and the manage-seating-refresh
            // recovery on /checkout all set it true to share QE-style
            // checkout-page behaviors. Without explicit exclusions for those
            // flows, this block would overwrite `payment.total` (correctly
            // set by their own accumulator paths earlier in this action)
            // with the gross ticket price + fee and double-bill the
            // customer. The exclusions below match every flow that has its
            // own `payment.total` computation and also sets the atom:
            //   - isCdtForTotal: handled by the CDT branch above.
            //   - isManageSeating / isManageAddons: prebooked-total clamp.
            //   - isUpgrade: reconciliation block.
            //   - isFrom === "on-hold-booking": ProceedToOnHoldAction sets
            //     the atom true and then runs the summary; its result has
            //     no real exchange-fee to bill, so the QE math is meaningless
            //     there.
            //   - isAddShuttleFlowAtom: AddShuttleTransferContinueClickAction
            //     (initial entry) and GetProcessBookingInitAction (/checkout
            //     recovery after redirect) both set this atom and the QE
            //     atom together. The add-shuttle settlement uses
            //     paymentStateAtom.total directly.
            //
            // Keeping QE in its own narrow branch also avoids the
            // /api/payment/session ZERO_PAYMENT_ERROR (500) the backend
            // throws when amount=0 and an active exchange-operation still
            // has a fee — we just never land on amount=0 here.
            const isQuickExchangeForTotal = (getRecoilStateValue("isQuickExchangeStateAtom") as boolean) || false;
            const isAddShuttleFlow = (getRecoilStateValue("isAddShuttleFlowAtom") as boolean) || false;
            const isQuickExchangeOnly =
                isQuickExchangeForTotal &&
                !isCdtForTotal &&
                !isManageSeating &&
                !isManageAddons &&
                !isUpgrade &&
                isFrom !== "on-hold-booking" &&
                !isAddShuttleFlow;
            if (isQuickExchangeOnly) {
                const sumPassengerAmounts = (passengers: any[] | undefined): number =>
                    (passengers || []).reduce((sum, p: any) => sum + (p?.amount || 0), 0);

                const ticketsOut = sumPassengerAmounts(
                    (paymentSummary as any)?.[TrainTypeEnum.outbound]?.passengerDetails?.successPassengers,
                );
                const ticketsIn = sumPassengerAmounts(
                    (paymentSummary as any)?.[TrainTypeEnum.inbound]?.passengerDetails?.successPassengers,
                );
                const feesOut =
                    (paymentSummary as any)?.[TrainTypeEnum.outbound]?.passengerDetails?.exchangeFee || 0;
                const feesIn =
                    (paymentSummary as any)?.[TrainTypeEnum.inbound]?.passengerDetails?.exchangeFee || 0;

                // Per-direction clamp on tickets handles the rare same-tier
                // downgrade case (`successPassengers[].amount` can be negative
                // when the new offer's admission costs less than the original).
                // We don't refund downward price differences — user just pays
                // the fee. Mirrors the chip's `max(0, priceDiff) + fee` clamp
                // so chip and checkout agree on the final number.
                const ticketsOutBilled = Math.max(0, ticketsOut);
                const ticketsInBilled = Math.max(0, ticketsIn);
                const newTotal = ticketsOutBilled + ticketsInBilled + feesOut + feesIn;
                paymentSummary.payment.subTotal = newTotal;
                paymentSummary.payment.total = newTotal;
                // Intentionally do NOT set `alreadyPaidAddonsAmount` — no
                // credit concept in QE, so the CDT credit banner stays hidden.
            }

            /**
             * On CDT checkout page, calculate already paid for addons if any. 
             * On Quick exchange page, calculate already paid for addons if any.                                   
             * On Upgrade To Premium page, calculate already paid for addons if any.                         
             * On Book It Again page, calculate already paid for addons if any.                         
            */
            if (!isEmpty(changeTripAlreadyPaidAddons) && calculateAlreadyPaidAddons) {
                const alreadyOutboundPainAddons = changeTripAlreadyPaidAddons?.outbound || 0
                const alreadyInboundPainAddons = changeTripAlreadyPaidAddons?.inbound || 0
                const totalAlreadyPaidAddons = alreadyOutboundPainAddons + alreadyInboundPainAddons
                if (totalAlreadyPaidAddons > 0) {
                    paymentSummary.payment.alreadyPaidAddonsAmount = totalAlreadyPaidAddons
                }
            }

            // bookingConfirmStatusAtom holds the derived status of the LAST-VIEWED
            // booking (set by GetBookingDetailStatusAction on the details page). It
            // is only meaningful for the booking-details surface, where viewing a
            // cancelled booking should render its summary as "Refunded Amount".
            // On a forward checkout (normal / on-hold / upgrade / change-date /
            // quick-exchange — none of which pass isFrom: "booking-details") this
            // atom can still hold a stale "REFUNDED" from a just-cancelled booking,
            // which wrongly flipped the new booking's total line from "Total to pay"
            // to "Refunded Amount". Gate the atom-read to the details surface so the
            // leak can't reach a forward checkout regardless of clear-timing; manage
            // flows still drive isRefund via otherDetails.isRefund (ConfirmAddonsAction).
            const bookingStatus = getRecoilStateValue("bookingConfirmStatusAtom")
            const isBookingDetailsSurface = isFrom === "booking-details"
            paymentSummary = {
                ...paymentSummary,
                otherDetails: {
                    ...paymentSummary?.otherDetails || {},
                    isRefund: paymentSummary?.otherDetails?.isRefund || (isBookingDetailsSurface && bookingStatus === "REFUNDED")
                }
            }

            // Upgrade-only invariant: payment.total must equal the sum of the
            // visible per-direction line items plus exchange fees. The displayed
            // amounts (outbound/inbound.passengerDetails.successPassengers[].amount)
            // are already net of alreadyPaid via the exchangeSuccessPassengers
            // overwrite above, so summing them yields the correct payable.
            // Without this, an earlier path can leave payment.total at the
            // un-discounted gross premium (sum of full premium prices) while the
            // line items show the discounted per-pax delta — i.e. Total = 238
            // beneath two "1 Adult ₿60" rows.
            //
            // Gated on isUpgrade so other flows (change-date, quick-exchange,
            // manage-seating, normal booking) are not affected. Only mutates
            // payment.total — subTotal is left to the upstream logic, which
            // some upgrade-specific paths (e.g. exchangeOperations-absent
            // fallback at line 994) define as pre-clamp gross to preserve the
            // no-refund golden rule.
            //
            // Reconcile only when line items carry non-zero values — those
            // are the discounted (post-alreadyPaid) per-passenger amounts
            // produced by the exchangeSuccessPassengers overwrite earlier in
            // this function. When line items are empty (e.g. the upstream
            // alreadyPaid clamp at line 994 set total=0 for a fee-only
            // upgrade with alreadyPaid > fees), the upstream value is the
            // source of truth — don't override it.
            if (isUpgrade) {
                const sumLines = (arr: any[] | undefined) =>
                    (arr || []).reduce((s: number, p: any) => s + (p?.amount || 0), 0);
                const outLines = sumLines(paymentSummary?.[TrainTypeEnum.outbound]?.passengerDetails?.successPassengers);
                const inLines = sumLines(paymentSummary?.[TrainTypeEnum.inbound]?.passengerDetails?.successPassengers);
                if (outLines + inLines > 0) {
                    const outFee = paymentSummary?.[TrainTypeEnum.outbound]?.passengerDetails?.exchangeFee || 0;
                    const inFee = paymentSummary?.[TrainTypeEnum.inbound]?.passengerDetails?.exchangeFee || 0;
                    paymentSummary.payment.total = Math.max(0, outLines + inLines + outFee + inFee);
                }
            }

            if (isUpgrade) {
                // Persist the fare-only upgrade base total so
                // mergeUpgradePaymentSummary can read it directly instead of
                // re-deriving it via (total - addons - seats).
                //
                // Why the subtraction was unsafe: the upgrade flow sets
                // CHANGE_DATE_FLOW=true (UpgradeToPremiumContinueClickAction),
                // which makes ConfirmAddonsAction SKIP its payment.total addon
                // bump. So after adding an addon, `total` stayed fare-only while
                // otherDetails.addons gained the addon — and the merge's first-run
                // `total - oldAddon` then double-removed the addon and FROZE the
                // base fare low (round-trip total dropped 120 -> 95 after an
                // add+remove). At this point `total` is the pure upgrade fare
                // (addons are layered on later by ConfirmAddonsAction), so it is
                // the correct base.
                paymentSummary.payment.upgradeBaseFareTotal = paymentSummary.payment.total
            }

            // Itemized discounts (R4 FSD): shown on the fare-paying checkout
            // surfaces only — normal booking, on-hold completion,
            // upgrade-to-premium, and change-date. applyNormalFlowDiscounts is
            // total-neutral (bumps the displayed rows to gross and subtracts the
            // same amount as line items; payment.total is untouched), so these
            // flows' gross/net deltas still reconcile. Self-gates to a no-op
            // when no real reduction exists.
            //
            // Excluded:
            //  - booking-details: read-only; the design itemizes discounts at
            //    checkout, not on the confirmed-booking view.
            //  - manage-seating / manage-addons / add-shuttle: the train tickets
            //    are already paid (their discount was shown at the original
            //    purchase) and payment.total here covers only the newly-added
            //    items, so re-itemizing the ticket discount would not reconcile.
            //  - plain quick-exchange (train swap, not change-date): out of scope.
            //  - multibooking: rendered by its own summary component.
            const isPlainQuickExchange = isFrom === "quick-exchange" && !isChangeDate
            const isDiscountEligibleCheckout =
                isFrom !== "booking-details" &&
                !isManageSeating &&
                !isManageAddons &&
                !isAddShuttleFlow &&
                !isPlainQuickExchange &&
                !(isMultiBooking || multiBooking)
            if (isDiscountEligibleCheckout) {
                // Offer sources for the discount name + gross lookup: the search
                // offers (forward booking) plus the per-leg upgrade exchange
                // offers (upgrade-to-premium, whose prebooked admission carries
                // neither reductionSummary nor originalPrice — both live here).
                const discountOfferSources = [
                    getRecoilStateValue("offerAtom"),
                    getRecoilStateValue("upgradeOutboundOffersAtom"),
                    getRecoilStateValue("upgradeInboundOffersAtom"),
                ]
                applyNormalFlowDiscounts(booking, allBookedOfferWithoutExchange, allTripWithoutExchange, paymentSummary, discountOfferSources)
            }

            updateRecoilState("checkoutPaymentSummaryAtom", paymentSummary)
        }
        return paymentSummary
    }
    override async run(): Promise<void> {
        try {
            const { isSplit = false, isFrom = "", onlyFulfillPax = false, isChangeDate = false, selectedPassengerForCancel = [], selectedPassengerForCancelInBound = [], isManageSeating = false, isManageAddons = false, changeTripAlreadyPaidAddons = {}, isUpgrade = false, isPrebookedOnly = false } = this.params!;
            let booking = this.params!;
            // booking-details renders a confirmed booking's state — ignore a stale
            // isChangeDateFlow="true" left by an abandoned upgrade/CDT flow, which
            // would otherwise force payment.total to provisionalPrice (=0).
            const isChangeDateFlow = isFrom === "booking-details"
                ? "false"
                : (localStorage.getItem(STORAGE_KEYS.CHANGE_DATE_FLOW) || "false");
            // booking-details shows the user's currently confirmed ticket. A pending
            // (PREBOOKED) exchange — upgrade/CDT the user started but didn't pay —
            // leaves the original admission as EXCHANGE_ONGOING and the unpaid target
            // as PREBOOKED. Without this normalization:
            //  - the EXCHANGE_ONGOING original carries the fare but is not counted
            //    as a live ticket by the passenger-price list,
            //  - PREBOOKED is filtered out by the booking-details filter below,
            //  - the exchangeOperations block then subtracts alreadyPaid from subTotal
            //    and payment.total goes negative (rendered as 0 — the reported bug).
            // Treat EXCHANGE_ONGOING as FULFILLED (what the user actually holds) and
            // clear exchangeOperations so the read-only view skips the delta logic.
            //
            // Detected from the ADMISSIONS via [[hasPendingExchange]], not from
            // `exchangeOperations[].status`: that status is "PREBOOKED" only while
            // the exchange sits in one particular state, and a booking whose
            // operation had moved on kept its EXCHANGE_ONGOING admissions, so
            // alreadyPaid still cancelled the fare and Total showed 0 against a
            // 50 Adult line.
            //
            // Safe to widen because a COMMITTED exchange leaves the originals
            // EXCHANGED, not EXCHANGE_ONGOING — the backend rule bookingEligibility
            // .ts's HISTORICAL_ADMISSION_STATUSES comment relies on. So a settled
            // booking has no EXCHANGE_ONGOING admission for the predicate to find
            // and this block is a no-op there. That is the whole argument: it reads
            // `booking.bookedOffers` RAW, ahead of GetAllOfferWithoutExchangeAction
            // below, so nothing that action filters bears on whether this fires.
            // (Do NOT cite hasPendingExchange's "originals fall back to FULFILLED"
            // line here: its own corrected NOTE says that describes a DISCARDED
            // exchange, not a committed one.)
            // Pinned by the "leaves a committed exchange alone" test.
            if (isFrom === "booking-details" && hasPendingExchange(booking)) {
                booking = {
                    ...booking,
                    bookedOffers: (booking.bookedOffers || []).map((offer: any) => ({
                        ...offer,
                        admissions: (offer.admissions || []).map((a: any) =>
                            a?.status === AdmissionStatusEnum.EXCHANGE_ONGOING
                                ? { ...a, status: AdmissionStatusEnum.FULFILLED }
                                : a
                        ),
                    })),
                    exchangeOperations: [],
                } as any;
            }

            // Restore trip directions if the backend flipped them post-/replace.
            // Defense-in-depth: callers (ApplyCheckoutUpsellAction, ConfirmSeat-
            // SelectionAction, PersistAddonsOnConfirmAction) recover at the
            // atom boundary; this re-runs locally so the summary stays self-
            // consistent even when a caller forgets to recover, or when
            // params arrive via a path that bypasses the atom.
            //
            // Booking-level recovery also patches `exchangeOperations[].trips`,
            // which preparePaymentSummary reads with isInbound/isOutbound
            // filters for the per-direction exchange-fee split.
            const priorSelectedTrainLegs = getRecoilStateValue<ISelectedTrainLegs>("selectedTrainLegsAtom");
            const recoveredBooking = recoverBookingTripDirections(booking as IBooking, priorSelectedTrainLegs);
            if (recoveredBooking && recoveredBooking !== (booking as IBooking)) {
                // recoveredBooking is already a full shallow clone of booking
                // with only `trips` / `exchangeOperations` modified; reassign
                // directly instead of re-spreading. The action's params type is
                // `IBooking & { extras }` — extras are preserved by the clone.
                booking = recoveredBooking as typeof booking;
            }

            let legSummary: any = {}
            const getAllOfferWithoutExchangeAction = new GetAllOfferWithoutExchangeAction()
            await getAllOfferWithoutExchangeAction.setParams(booking.bookedOffers)
            await getAllOfferWithoutExchangeAction.execute();
            let allBookedOfferWithoutExchange: IBookedOffer[] = [];
            let allTripWithoutExchange: IBookingTypeTrip[] = [];
            if (isActionRunSuccessfully(getAllOfferWithoutExchangeAction)) {
                allBookedOfferWithoutExchange = isSplit ? booking.bookedOffers : getAllOfferWithoutExchangeAction.result!
                const getAllTripWithoutExchangeAction = new GetAllTripWithoutExchangeAction()
                getAllTripWithoutExchangeAction.setParams({ bookedOffers: allBookedOfferWithoutExchange, trips: booking.trips })
                await getAllTripWithoutExchangeAction.execute();
                if (isActionRunSuccessfully(getAllTripWithoutExchangeAction)) {
                    allTripWithoutExchange = getAllTripWithoutExchangeAction.result!
                }
            }

            // Pending exchange (quick-exchange / CDT before payment): the booking
            // holds BOTH the exchanged-out (EXCHANGE_ONGOING) offer and its
            // exchanged-in (PREBOOKED) replacement. GetAllOfferWithoutExchangeAction
            // only drops EXCHANGED — not EXCHANGE_ONGOING — so both survive here and
            // the exchanged-out trip + its addons get shown/charged a second time on
            // any summary refresh (e.g. after a seat change: two trip cards, doubled
            // extras). Drop ONLY the exchanged-out offers (keep FULFILLED other legs
            // and the PREBOOKED replacement) so round trips are unaffected. Upgrade
            // has its own delta handling; booking-details normalises EXCHANGE_ONGOING
            // → FULFILLED above, so neither triggers this.
            if (!isUpgrade) {
                const hasExchangeOngoing = allBookedOfferWithoutExchange.some(
                    offer => offer.admissions?.some(admission => admission.status === AdmissionStatusEnum.EXCHANGE_ONGOING)
                );
                const hasPrebooked = allBookedOfferWithoutExchange.some(
                    offer => offer.admissions?.some(admission => admission.status === AdmissionStatusEnum.PREBOOKED)
                );
                if (hasExchangeOngoing && hasPrebooked) {
                    const withoutExchangedOut = allBookedOfferWithoutExchange.filter(
                        offer => !offer.admissions?.some(admission => admission.status === AdmissionStatusEnum.EXCHANGE_ONGOING)
                    );
                    if (withoutExchangedOut.length > 0) {
                        allBookedOfferWithoutExchange = withoutExchangedOut;
                        allTripWithoutExchange = allTripWithoutExchange.filter(trip =>
                            withoutExchangedOut.some(offer => offer.tripCoverage?.coveredTripId === trip.id)
                        );
                    }
                }
            }

            if (isPrebookedOnly) {
                const { prebookedOffers, prebookedTrips } = this.filterPrebookedOffersAndTrips(allBookedOfferWithoutExchange, allTripWithoutExchange);
                allBookedOfferWithoutExchange = prebookedOffers;
                allTripWithoutExchange = prebookedTrips;
            }

            // For booking-details: exclude offers where every admission is PREBOOKED. (e.g. shuttle) that were never paid.
            if (isFrom === "booking-details") {
                allBookedOfferWithoutExchange = allBookedOfferWithoutExchange.filter(
                    offer => !offer.admissions.every(admission => admission.status === "PREBOOKED")
                );
                allTripWithoutExchange = allTripWithoutExchange.filter(trip =>
                    allBookedOfferWithoutExchange.some(offer => offer.tripCoverage.coveredTripId === trip.id)
                );
            }

            const legCount = allTripWithoutExchange.flatMap(d => d.legs)
            if (legCount.length <= 2 && legCount.length > 0) {
                updateRecoilState("defaultActiveKeyForTripSummaryAtom", legCount.map(d => {
                    return { legId: d.id, activeKey: ["1"] }
                }))
            } else {
                updateRecoilState("defaultActiveKeyForTripSummaryAtom", [])
            }

            if (isFrom === "quick-exchange") {
                if (isChangeDate) {
                    // Change date: rewrite the trip-summary legs from the PREBOOKED
                    // (exchanged-in) offers — they carry the real catalog product,
                    // while the legs the change-date train-offer screen left here
                    // are built from the OFFER trip with no booked offers, so they
                    // hold productName "" and a raw product code as trainClass.
                    //
                    // Only when prebooked offers AND trips exist. The fallback the
                    // change-date branch below uses is reached exactly when there
                    // are none — and then allTripWithoutExchange still holds the
                    // exchanged-OUT trip (the drop at the top of run() is gated on
                    // a PREBOOKED offer existing), which would put the old journey
                    // back on the card. When it does skip, the card simply keeps
                    // today's behaviour.
                    //
                    // legSummary seeds from the atom so a direction that was NOT
                    // changed keeps its legs (one-way change on a round trip).
                    const { prebookedOffers, prebookedTrips } = this.filterPrebookedOffersAndTrips(allBookedOfferWithoutExchange, allTripWithoutExchange);
                    if (prebookedOffers.length > 0 && prebookedTrips.length > 0) {
                        const cdtTrips = preferTrainTrips(prebookedTrips);
                        const latestOutboundTrip = [...cdtTrips].reverse().find(trip => trip?.direction === TripTypeEnum.OUT_BOUND);
                        const latestInboundTrip = [...cdtTrips].reverse().find(trip => trip?.direction === TripTypeEnum.IN_BOUND);
                        legSummary = priorSelectedTrainLegs || null;
                        if (latestOutboundTrip) {
                            legSummary = await this.updateLegTripSummary(latestOutboundTrip.legs, prebookedOffers, TrainTypeEnum.outbound, legSummary, booking);
                        }
                        if (latestInboundTrip) {
                            await this.updateLegTripSummary(latestInboundTrip.legs, prebookedOffers, TrainTypeEnum.inbound, legSummary, booking);
                        }
                    }
                }
                else {
                    const findOutbOundTrip: IBookingTypeTrip | undefined = preferTrainTrips(allTripWithoutExchange).find(trip => trip.direction === TripTypeEnum.OUT_BOUND);
                    if (findOutbOundTrip) {
                        legSummary = await this.updateLegTripSummary(findOutbOundTrip.legs, allBookedOfferWithoutExchange, TrainTypeEnum.outbound, null, booking)
                    }
                }
            } else {
                if (allTripWithoutExchange.length === 2 && allTripWithoutExchange[0].direction === TripTypeEnum.IN_BOUND && allTripWithoutExchange[1].direction === TripTypeEnum.OUT_BOUND) {
                    // Swap the trips
                    const temp = allTripWithoutExchange[0];
                    allTripWithoutExchange[0] = allTripWithoutExchange[1];
                    allTripWithoutExchange[1] = temp;
                }


                if (isChangeDateFlow === "true") {
                    const { prebookedOffers, prebookedTrips } = this.filterPrebookedOffersAndTrips(allBookedOfferWithoutExchange, allTripWithoutExchange);
                    const cdtOffers = prebookedOffers.length > 0 ? prebookedOffers : allBookedOfferWithoutExchange;
                    const cdtTrips = prebookedTrips.length > 0 ? prebookedTrips : allTripWithoutExchange;
                    const latestOutboundTrip = [...preferTrainTrips(cdtTrips)].reverse().find(trip => trip?.direction === TripTypeEnum.OUT_BOUND);
                    const latestInboundTrip = [...preferTrainTrips(cdtTrips)].reverse().find(trip => trip?.direction === TripTypeEnum.IN_BOUND);

                    let legSummary: any = null;
                    if (latestOutboundTrip) {
                        legSummary = await this.updateLegTripSummary(latestOutboundTrip.legs, cdtOffers, TrainTypeEnum.outbound, null, booking);
                    }

                    if (latestInboundTrip) {
                        await this.updateLegTripSummary(latestInboundTrip.legs, cdtOffers, TrainTypeEnum.inbound, legSummary, booking);
                    }

                    const latestTrips = [latestOutboundTrip, latestInboundTrip].filter(Boolean) as IBookingTypeTrip[];
                    await this.preparePaymentSummary(booking, cdtOffers, latestTrips, {}, isSplit, onlyFulfillPax, latestTrips, cdtOffers, false, true, false, false, false, false, isFrom);
                }
                else {
                    // Standalone shuttle-only trips (added via onward travel) are
                    // itemized in the payment details — keep them off the Trip
                    // summary cards. Trips that mix train + bus legs still show whole.
                    // Shuttle-only bookings (standalone Shuttle flow) keep their card.
                    const summaryTrips = preferTrainTrips(allTripWithoutExchange);
                    const outboundTrips = summaryTrips.filter(trip => trip.direction === TripTypeEnum.OUT_BOUND);
                    const combinedOutboundLegs = outboundTrips.flatMap(trip => trip.legs);
                    if (combinedOutboundLegs.length > 0) {
                        legSummary = await this.updateLegTripSummary(combinedOutboundLegs, allBookedOfferWithoutExchange, TrainTypeEnum.outbound, null, booking);
                    }

                    const inboundTrips = summaryTrips.filter(trip => trip.direction === TripTypeEnum.IN_BOUND);
                    const combinedInboundLegs = inboundTrips.flatMap(trip => trip.legs);
                    if (combinedInboundLegs.length > 0) {
                        legSummary = await this.updateLegTripSummary(combinedInboundLegs, allBookedOfferWithoutExchange, TrainTypeEnum.inbound, legSummary, booking);
                    }
                }
            }

            // check if booking is pass booking
            const passObjectType = booking.bookedOffers[0]?.admissions[0].indicatedConsumption?.unit?.objectType || ""
            if (passObjectType) {
                this.preparePaymentSummary(booking, [], [], {}, isSplit, onlyFulfillPax, [], [], false, false, false, false, false, false, isFrom)
            }

            //Prepare payment summary for checkout
            if (booking.trips.length > 0) {
                if (isFrom === "quick-exchange" && !isChangeDate) {
                    const findPreBookQuickExchangeOffer: IBookedOffer | undefined = allBookedOfferWithoutExchange.find(trip => trip.admissions.find(admission => admission.status === "PREBOOKED"));
                    if (findPreBookQuickExchangeOffer) {
                        const findPreBookTripFromOffer: IBookingTypeTrip | undefined = allTripWithoutExchange.find(trip => trip.id === findPreBookQuickExchangeOffer.tripCoverage.coveredTripId);
                        if (findPreBookTripFromOffer) {
                            await this.preparePaymentSummary(booking, [findPreBookQuickExchangeOffer], [findPreBookTripFromOffer],
                                changeTripAlreadyPaidAddons, isSplit, onlyFulfillPax, [], [], false, false, true, false, false, false, isFrom);
                        }
                    }
                }

                else if (isFrom === "on-hold-booking") {
                    const onHoldOffers: IBookedOffer[] =
                        allBookedOfferWithoutExchange.filter(offer => offer.admissions?.some(admission => admission.status === "ON_HOLD"));

                    if (onHoldOffers.length > 0) {
                        const coveredTripIds = new Set(onHoldOffers.map(offer => offer.tripCoverage?.coveredTripId).filter((id): id is string => !!id));

                        const onHoldTrips: IBookingTypeTrip[] =
                            allTripWithoutExchange.filter(trip => coveredTripIds.has(trip.id));

                        if (onHoldTrips.length > 0) {
                            await this.preparePaymentSummary(booking, onHoldOffers, onHoldTrips, changeTripAlreadyPaidAddons, isSplit, onlyFulfillPax, [], [], false, false, true, true, false, false, isFrom);
                        }
                    }
                }
                else if (isUpgrade) {
                    const { filteredTrips, filteredBookedOffers } = filterTripsAndOffersWithPrebookStatus(
                        allTripWithoutExchange,
                        allBookedOfferWithoutExchange
                    );

                    if (selectedPassengerForCancel.length > 0 && selectedPassengerForCancelInBound.length > 0) {
                        // Get unique passenger IDs from both arrays
                        const uniqueSelectedPassengerIds = Array.from(new Set([
                            ...selectedPassengerForCancel,
                            ...selectedPassengerForCancelInBound
                        ]));

                        // Find outbound and inbound trips
                        const findOutboundTrip: IBookingTypeTrip | undefined = preferTrainTrips(filteredTrips).find(trip => trip.direction === TripTypeEnum.OUT_BOUND);
                        const findInboundTrip: IBookingTypeTrip | undefined = preferTrainTrips(filteredTrips).find(trip => trip.direction === TripTypeEnum.IN_BOUND);

                        // Filter booking passengers to only include selected ones
                        const filteredBooking = {
                            ...booking,
                            passengers: booking.passengers.filter(passenger =>
                                uniqueSelectedPassengerIds.includes(passenger.id)
                            ),
                            bookedOffers: booking.bookedOffers.map(offer => ({
                                ...offer,
                                admissions: offer.admissions.filter(admission =>
                                    admission.passengerIds.some(id => uniqueSelectedPassengerIds.includes(id))
                                )
                            }))
                        };

                        const filteredOBBooking = {
                            ...booking,
                            passengers: booking.passengers.filter(passenger =>
                                selectedPassengerForCancel.includes(passenger.id)
                            ),
                            bookedOffers: booking.bookedOffers.map(offer => ({
                                ...offer,
                                admissions: offer.admissions.filter(admission =>
                                    admission.passengerIds.some(id => selectedPassengerForCancel.includes(id))
                                )
                            }))
                        };

                        const filteredIBBooking = {
                            ...booking,
                            passengers: booking.passengers.filter(passenger =>
                                selectedPassengerForCancelInBound.includes(passenger.id)
                            ),
                            bookedOffers: booking.bookedOffers.map(offer => ({
                                ...offer,
                                admissions: offer.admissions.filter(admission =>
                                    admission.passengerIds.some(id => selectedPassengerForCancelInBound.includes(id))
                                )
                            }))
                        };

                        // Update leg summaries for both directions if found
                        let legSummary: { outbound?: any; inbound?: any } = {};
                        if (findOutboundTrip) {
                            legSummary.outbound = await this.updateLegTripSummary(
                                findOutboundTrip.legs,
                                filteredBookedOffers,
                                TrainTypeEnum.outbound,
                                null,
                                filteredOBBooking,
                                true
                            );
                        }
                        if (findInboundTrip) {
                            legSummary.inbound = await this.updateLegTripSummary(
                                findInboundTrip.legs,
                                filteredBookedOffers,
                                TrainTypeEnum.inbound,
                                legSummary.outbound,
                                filteredIBBooking,
                                true
                            );
                        }
                        await this.preparePaymentSummary(filteredBooking, filteredBookedOffers, filteredTrips,
                            changeTripAlreadyPaidAddons, isSplit, false,
                            filteredTrips, filteredBookedOffers, true, false, true, false, false, false, isFrom);
                    }
                    else if (selectedPassengerForCancel.length > 0) {
                        const findOutboundTrip: IBookingTypeTrip | undefined = preferTrainTrips(filteredTrips).find(trip => trip.direction === TripTypeEnum.OUT_BOUND);
                        if (findOutboundTrip) {
                            const findOutboundOffers: IBookedOffer[] = filteredBookedOffers.filter(offer => 
                                offer.tripCoverage.coveredTripId === findOutboundTrip.id
                            );
                            if (findOutboundOffers.length > 0) {
                                // Filter booking passengers to only include selected outbound passengers
                                const filteredBooking = {
                                    ...booking,
                                    passengers: booking.passengers.filter(passenger =>
                                        selectedPassengerForCancel.includes(passenger.id)
                                    ),
                                    bookedOffers: booking.bookedOffers.map(offer => ({
                                        ...offer,
                                        admissions: offer.admissions.filter(admission =>
                                            admission.passengerIds.some(id => selectedPassengerForCancel.includes(id))
                                        )
                                    }))
                                };
                                legSummary = await this.updateLegTripSummary(findOutboundTrip.legs, filteredBookedOffers, TrainTypeEnum.outbound, null, filteredBooking, true)
                                await this.preparePaymentSummary(filteredBooking, findOutboundOffers, [findOutboundTrip], changeTripAlreadyPaidAddons, isSplit, false, [findOutboundTrip], findOutboundOffers, true, false, true, false, false, false, isFrom);
                            }
                        }
                    }
                    else if (selectedPassengerForCancelInBound.length > 0) {
                        const findInboundTrip: IBookingTypeTrip | undefined = preferTrainTrips(filteredTrips).find(trip => trip.direction === TripTypeEnum.IN_BOUND);
                        if (findInboundTrip) {
                            const findInboundOffers: IBookedOffer[] = filteredBookedOffers.filter(offer => 
                                offer.tripCoverage.coveredTripId === findInboundTrip.id
                            );
                            if (findInboundOffers.length > 0) {
                                // Filter booking passengers to only include selected inbound passengers
                                const filteredBooking = {
                                    ...booking,
                                    passengers: booking.passengers.filter(passenger =>
                                        selectedPassengerForCancelInBound.includes(passenger.id)
                                    ),
                                    bookedOffers: booking.bookedOffers.map(offer => ({
                                        ...offer,
                                        admissions: offer.admissions.filter(admission =>
                                            admission.passengerIds.some(id => selectedPassengerForCancelInBound.includes(id))
                                        )
                                    }))
                                };
                                legSummary = await this.updateLegTripSummary(findInboundTrip.legs, filteredBookedOffers, TrainTypeEnum.inbound, null, filteredBooking, true)
                                await this.preparePaymentSummary(filteredBooking, findInboundOffers, [findInboundTrip], {}, isSplit, false, [findInboundTrip], findInboundOffers, true, false, true, false, false, false, isFrom);
                            }
                        }
                    }
                }
                else if (isPrebookedOnly) {
                    const { filteredTrips, filteredBookedOffers } = filterTripsAndOffersWithPrebookStatus(
                        allTripWithoutExchange,
                        allBookedOfferWithoutExchange
                    );

                    const combinedOutboundLegs = preferTrainTrips(filteredTrips).filter(trip => trip.direction === TripTypeEnum.OUT_BOUND).flatMap(trip => trip.legs);
                    const combinedInboundLegs = preferTrainTrips(filteredTrips).filter(trip => trip.direction === TripTypeEnum.IN_BOUND).flatMap(trip => trip.legs);

                    let legSummary: { outbound?: any; inbound?: any } = {};
                    if (combinedOutboundLegs.length > 0) {
                        legSummary.outbound = await this.updateLegTripSummary(
                            combinedOutboundLegs,
                            filteredBookedOffers,
                            TrainTypeEnum.outbound,
                            null,
                            booking,
                            false
                        );
                    }
                    if (combinedInboundLegs.length > 0) {
                        legSummary.inbound = await this.updateLegTripSummary(
                            combinedInboundLegs,
                            filteredBookedOffers,
                            TrainTypeEnum.inbound,
                            legSummary.outbound,
                            booking,
                            false
                        );
                    }
                    // Forward isManageAddons (was hardcoded false) so a caller
                    // adding a paid extra to an ALREADY-SETTLED booking reaches
                    // the settled-booking delta block ("(isManageAddons ||
                    // isManageSeating) && hasSettledItems"), which sets
                    // total = prebookedTotal and credits the rest as alreadyPaid.
                    // Without it the settled fare is re-derived from scratch and
                    // the new extra never lands in the payable total.
                    // Callers passing isPrebookedOnly alone (Add-shuttle-transfer)
                    // are unaffected: isManageAddons defaults to false for them.
                    // NOTE: no caller currently passes BOTH — the only one that
                    // would is onwardSummaryParams' change-date branch, which is
                    // itself unreachable (see its REACHABILITY note). This
                    // forwarding is correct but inert today.
                    await this.preparePaymentSummary(booking, filteredBookedOffers, filteredTrips,
                        changeTripAlreadyPaidAddons, isSplit, false,
                        filteredTrips, filteredBookedOffers, false, false, true, false, false, isManageAddons, isFrom);
                }
                else if (isChangeDate) {
                    const { prebookedOffers, prebookedTrips } = this.filterPrebookedOffersAndTrips(allBookedOfferWithoutExchange, allTripWithoutExchange);
                    const cdtOffers = prebookedOffers.length > 0 ? prebookedOffers : allBookedOfferWithoutExchange;
                    const cdtTrips = prebookedTrips.length > 0 ? prebookedTrips : allTripWithoutExchange;

                    if (selectedPassengerForCancel.length > 0 && selectedPassengerForCancelInBound.length > 0) {
                        // Get unique passenger IDs from both arrays
                        const uniqueSelectedPassengerIds = Array.from(new Set([
                            ...selectedPassengerForCancel,
                            ...selectedPassengerForCancelInBound
                        ]));

                        // Filter booking passengers to only include selected ones
                        const filteredBooking = {
                            ...booking,
                            passengers: booking.passengers.filter(passenger =>
                                uniqueSelectedPassengerIds.includes(passenger.id)
                            ),
                            bookedOffers: booking.bookedOffers.map(offer => ({
                                ...offer,
                                admissions: offer.admissions.filter(admission =>
                                    admission.passengerIds.some(id => uniqueSelectedPassengerIds.includes(id))
                                )
                            }))
                        };

                        await this.preparePaymentSummary(filteredBooking, cdtOffers, cdtTrips, changeTripAlreadyPaidAddons, isSplit, false, [], [], false, true, true, false, false, false, isFrom);
                    }
                    else if (selectedPassengerForCancel.length > 0) {
                        const findOutboundTrip: IBookingTypeTrip | undefined = preferTrainTrips(cdtTrips).find(trip => trip.direction === TripTypeEnum.OUT_BOUND);
                        if (findOutboundTrip) {
                            const findOutboundOffer: IBookedOffer | undefined = cdtOffers.find(offer =>
                                findOutboundTrip.legs.some(leg => offer.tripCoverage.coveredLegIds.includes(leg.id))
                            );
                            if (findOutboundOffer) {
                                // Filter booking passengers to only include selected outbound passengers
                                const filteredBooking = {
                                    ...booking,
                                    passengers: booking.passengers.filter(passenger =>
                                        selectedPassengerForCancel.includes(passenger.id)
                                    ),
                                    bookedOffers: booking.bookedOffers.map(offer => ({
                                        ...offer,
                                        admissions: offer.admissions.filter(admission =>
                                            admission.passengerIds.some(id => selectedPassengerForCancel.includes(id))
                                        )
                                    }))
                                };

                                await this.preparePaymentSummary(filteredBooking, [findOutboundOffer], [findOutboundTrip], changeTripAlreadyPaidAddons, isSplit, false, [], [], false, true, true, false, false, false, isFrom);
                            }
                        }
                    }
                    else if (selectedPassengerForCancelInBound.length > 0) {
                        const findInboundTrip: IBookingTypeTrip | undefined = preferTrainTrips(cdtTrips).find(trip => trip.direction === TripTypeEnum.IN_BOUND);
                        if (findInboundTrip) {
                            const findInboundOffer: IBookedOffer | undefined = cdtOffers.find(offer =>
                                findInboundTrip.legs.some(leg => offer.tripCoverage.coveredLegIds.includes(leg.id))
                            );
                            if (findInboundOffer) {
                                // Filter booking passengers to only include selected inbound passengers
                                const filteredBooking = {
                                    ...booking,
                                    passengers: booking.passengers.filter(passenger =>
                                        selectedPassengerForCancelInBound.includes(passenger.id)
                                    ),
                                    bookedOffers: booking.bookedOffers.map(offer => ({
                                        ...offer,
                                        admissions: offer.admissions.filter(admission =>
                                            admission.passengerIds.some(id => selectedPassengerForCancelInBound.includes(id))
                                        )
                                    }))
                                };

                                await this.preparePaymentSummary(filteredBooking, [findInboundOffer], [findInboundTrip], changeTripAlreadyPaidAddons, isSplit, false, [], [], false, true, true, false, false, false, isFrom);
                            }
                        }
                    }
                }
                else if (isManageSeating) {
                    // For manage seating: compute addons + seat fees from the existing booking
                    // so the user can see what they already have selected.
                    // calculateAlreadyPaidAddons=false lets the addon/seat-fee accumulators run;
                    // isManageSeating=true (13th arg) allows FULFILLED reservations to be included.
                    const paymentSummary = await this.preparePaymentSummary(booking, allBookedOfferWithoutExchange, allTripWithoutExchange, changeTripAlreadyPaidAddons, isSplit, onlyFulfillPax, [], [], false, false, false, false, true, false, isFrom);
                    // Clear passenger fare breakdown (not shown in manage-seating context).
                    // Preserve addons and seat fees so the summary reflects existing selections.
                    let updatedPaymentSummary: any = {
                        ...paymentSummary,
                        outbound: {
                            ...paymentSummary?.outbound || {},
                            passengerDetails: {}
                        },
                    }
                    if (paymentSummary?.inbound) {
                        updatedPaymentSummary = {
                            ...updatedPaymentSummary,
                            inbound: {
                                ...paymentSummary?.inbound || {},
                                passengerDetails: {}
                            },
                        }
                    }
                    updateRecoilState("checkoutPaymentSummaryAtom", updatedPaymentSummary)
                }
                else if (isChangeDateFlow !== "true") {
                    await this.preparePaymentSummary(booking, allBookedOfferWithoutExchange, allTripWithoutExchange, changeTripAlreadyPaidAddons, isSplit, onlyFulfillPax, undefined, undefined, false, false, false, false, false, isManageAddons, isFrom);
                }
            }
        } catch (error: any) {
            console.error("Error fetching Data:", error);
        }
    }
}

export const prepareBookingSummaryAction = new PrepareBookingSummaryAction()
